import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Alert,
    Skeleton,
    Breadcrumbs,
    Collapse,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
    Tooltip,
    CircularProgress,
    Paper,
    TextField,
    Stack,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    Grid,
    Autocomplete,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as AcceptIcon,
    Cancel as RejectIcon,
    Edit as EditIcon,
    Info as InfoIcon,
    PlayArrow as ProcessIcon,
    TableChart as TableIcon,
    Pending as PendingIcon,
    Delete as DeleteIcon,
    GetApp as DownloadIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
    getProject,
    getProjectFiles,
    processLegacyFile,
    saveMappingDecision,
    getMappingProcessingStatus,
    deleteMappings,
    buildMappingExportCsvUrl,
    getMappingResultsPaginated,
    getMappingResultsDetails,
    getMappingDiagnosticsCatalog,
    getMappingDiagnosticLiveTraceProgress,
    getTablesBySource,
    getColumnsBySource,
    startMappingDiagnosticLiveTrace,
} from '../services/api';

const LEGACY_SYSTEMS = ['DFSS', 'IDW'];
const LEGACY_DEFINITION_PREVIEW_LENGTH = 30;
const CATALYST_LAYER_SYSTEMS = {
    L2: 'IDDS_L2',
    L3: 'IDDS_L3',
};
const MATCH_STAGE_OPTIONS = [
    { value: 'user_preference', label: 'User Preference' },
    { value: 'same_theme_priority', label: 'Same Theme Priority' },
    { value: 'other_priority_tables', label: 'Other Priority Tables' },
    { value: 'same_theme_all', label: 'Same Theme All Tables' },
    { value: 'combined_theme_priority', label: 'Combined Theme Priority' },
    { value: 'combined_theme_all', label: 'Combined Theme All Tables' },
    { value: 'other_theme_priority', label: 'Other Theme Priority' },
    { value: 'other_theme_all', label: 'Other Theme All Tables' },
    { value: 'all_tables', label: 'All Tables' },
    { value: 'fallback', label: 'Fallback' },
    { value: 'manual', label: 'Manual Selection' },
];
const ANALYTICS_SEGMENT_META = {
    exact: { label: 'Exact Match', color: '#1976d2' },
    semantic_high_confidence: { label: 'Semantic >=80%', color: '#2e7d32' },
    manual: { label: 'Manual', color: '#6d4c41' },
    semantic_low_confidence: { label: 'Semantic <80%', color: '#ed6c02' },
    no_match: { label: 'No Match', color: '#d32f2f' },
};
const STAGE_COUNT_CHIPS = [
    { key: 'stage_1_matches', label: 'Same+Priority', color: 'success', info: 'Matches found within the same theme and priority bucket.' },
    { key: 'stage_2_matches', label: 'Other Priority', color: 'warning', info: 'Matches found in a different priority bucket.' },
    { key: 'stage_3_matches', label: 'Same+All', color: 'info', info: 'Matches found within the same theme across all tables.' },
    { key: 'stage_4_matches', label: 'Combined+All', color: 'secondary', info: 'Combined theme matches found across all tables.' },
    { key: 'stage_5_matches', label: 'Other+All', color: 'default', info: 'Matches found in other themes across all tables.' },
    { key: 'stage_6_matches', label: 'All Tables', color: 'default', info: 'Fallback search across all available tables.' },
];
const DIAGNOSTIC_LAYERS = ['L2', 'L3'];

function SchemaMapperPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // State
    const [project, setProject] = useState(null);
    const [files, setFiles] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [mappingData, setMappingData] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState(null);
    const [batchTimingDialogOpen, setBatchTimingDialogOpen] = useState(false);
    const [mappingSummaryOpen, setMappingSummaryOpen] = useState(false);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [manualDialogOpen, setManualDialogOpen] = useState(false);
    const [manualDialogColumn, setManualDialogColumn] = useState(null);
    const [manualDialogLayer, setManualDialogLayer] = useState('L2');
    const [manualDialogPath, setManualDialogPath] = useState('semantic');
    const [manualDialogSourceMatch, setManualDialogSourceMatch] = useState(null);
    const [manualTables, setManualTables] = useState([]);
    const [manualColumns, setManualColumns] = useState([]);
    const [manualTable, setManualTable] = useState(null);
    const [manualColumn, setManualColumn] = useState(null);
    const [manualLoadingTables, setManualLoadingTables] = useState(false);
    const [manualLoadingColumns, setManualLoadingColumns] = useState(false);
    const [manualSaving, setManualSaving] = useState(false);
    const [activeView, setActiveView] = useState('results');
    const [diagnosticCatalogLoading, setDiagnosticCatalogLoading] = useState(false);
    const [diagnosticTargetCatalogLoading, setDiagnosticTargetCatalogLoading] = useState(false);
    const [diagnosticFieldLoading, setDiagnosticFieldLoading] = useState(false);
    const [diagnosticReadyFileId, setDiagnosticReadyFileId] = useState(null);
    const [diagnosticSourceTables, setDiagnosticSourceTables] = useState([]);
    const [diagnosticSourceTable, setDiagnosticSourceTable] = useState(null);
    const [diagnosticSourceColumn, setDiagnosticSourceColumn] = useState(null);
    const [diagnosticSourceTableInput, setDiagnosticSourceTableInput] = useState('');
    const [diagnosticSourceColumnInput, setDiagnosticSourceColumnInput] = useState('');
    const [diagnosticFieldData, setDiagnosticFieldData] = useState(null);
    const [diagnosticTargetLayer, setDiagnosticTargetLayer] = useState('L2');
    const [diagnosticTargetTablesByLayer, setDiagnosticTargetTablesByLayer] = useState({ L2: [], L3: [] });
    const [diagnosticTargetTableMetaByLayer, setDiagnosticTargetTableMetaByLayer] = useState({ L2: {}, L3: {} });
    const [diagnosticTargetColumnsByLayer, setDiagnosticTargetColumnsByLayer] = useState({ L2: {}, L3: {} });
    const [diagnosticTargetColumnsCache, setDiagnosticTargetColumnsCache] = useState({});
    const [diagnosticTargetTable, setDiagnosticTargetTable] = useState(null);
    const [diagnosticTargetColumn, setDiagnosticTargetColumn] = useState(null);
    const [diagnosticTargetColumns, setDiagnosticTargetColumns] = useState([]);
    const [diagnosticTargetLoadingColumns, setDiagnosticTargetLoadingColumns] = useState(false);
    const [diagnosticTargetTableInput, setDiagnosticTargetTableInput] = useState('');
    const [diagnosticTargetColumnInput, setDiagnosticTargetColumnInput] = useState('');
    const [diagnosticSemanticScore, setDiagnosticSemanticScore] = useState(null);
    const [diagnosticSemanticScoreLoading, setDiagnosticSemanticScoreLoading] = useState(false);
    const [diagnosticTargetTrace, setDiagnosticTargetTrace] = useState(null);
    const [diagnosticTargetTraceLoading, setDiagnosticTargetTraceLoading] = useState(false);
    const [diagnosticLiveTrace, setDiagnosticLiveTrace] = useState(null);
    const [diagnosticLiveTraceLoading, setDiagnosticLiveTraceLoading] = useState(false);
    const [diagnosticLiveReplayIndex, setDiagnosticLiveReplayIndex] = useState(0);
    const [diagnosticLivePipelineRunning, setDiagnosticLivePipelineRunning] = useState(false);
    const [diagnosticLiveStatusMessage, setDiagnosticLiveStatusMessage] = useState('');
    const [diagnosticLiveProgress, setDiagnosticLiveProgress] = useState({ current: null, total: null, percent: null });
    const [diagnosticLiveExpandedPanel, setDiagnosticLiveExpandedPanel] = useState('exact_lookup');
    const [diagnosticLiveRunId, setDiagnosticLiveRunId] = useState(null);

    // Pagination & Filters
    const [page, setPage] = useState(1);
    const [pageSize] = useState(15);
    const [tableFilter, setTableFilter] = useState('');
    const [columnFilter, setColumnFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [matchStageFilter, setMatchStageFilter] = useState('');
    const [rowLayerFilter, setRowLayerFilter] = useState({});
    const [rowMatchPathFilter, setRowMatchPathFilter] = useState({});
    const [rowVisibleMatchCounts, setRowVisibleMatchCounts] = useState({});
    const [defDialogOpen, setDefDialogOpen] = useState(false);
    const [defDialogContent, setDefDialogContent] = useState({ title: '', definition: '' });
    const pollingRef = useRef(null);
    const previewVersionRef = useRef(0);
    const resultsRequestRef = useRef(0);
    const preferredSelectionAppliedRef = useRef(false);

    const getLegacyDefinitionPreview = (definition) => {
        if (!definition) return '-';
        if (definition.length <= LEGACY_DEFINITION_PREVIEW_LENGTH) return definition;
        return `${definition.slice(0, LEGACY_DEFINITION_PREVIEW_LENGTH)}...`;
    };

    const normalizeIdentifier = (value) => String(value || '').trim().toLowerCase();
    const normalizeMatchPath = (value, fallback = '') => {
        const normalized = String(value || '').trim().toLowerCase();
        if (['exact', 'semantic', 'manual'].includes(normalized)) return normalized;
        return fallback;
    };
    const getCandidateCombinedIndex = (candidate) => {
        if (Number.isInteger(candidate?.combinedIndex)) return candidate.combinedIndex;
        if (Number.isInteger(candidate?._combined_index)) return candidate._combined_index;
        return null;
    };
    const getDecisionTimestamp = (decision) => {
        const parsed = Date.parse(decision?.decided_at || '');
        return Number.isFinite(parsed) ? parsed : 0;
    };
    const normalizeAcceptedCandidate = (candidate, fallbackLayer = '') => {
        const candidateMatchPath = normalizeMatchPath(candidate?.match_path || candidate?.matchPath, '');
        const isManual = String(candidate?.match_stage || '').toLowerCase() === 'manual'
            || candidateMatchPath === 'manual'
            || String(candidate?.selection_mode || '').toLowerCase() === 'manual';
        return {
            ...candidate,
            catalyst_table: candidate?.catalyst_table || '',
            catalyst_column: candidate?.catalyst_column || '',
            catalyst_attribute: candidate?.catalyst_attribute || '',
            catalyst_definition: candidate?.catalyst_definition || '',
            catalyst_data_type: candidate?.catalyst_data_type || '',
            catalyst_layer: candidate?.catalyst_layer || fallbackLayer,
            match_stage: isManual ? 'manual' : (candidate?.match_stage || ''),
            match_path: candidateMatchPath || (isManual ? 'manual' : ''),
            vector_score: isManual
                ? 1
                : (typeof candidate?.vector_score === 'number' ? candidate.vector_score : 0),
            llm_confidence: isManual
                ? 1
                : (typeof candidate?.llm_confidence === 'number' ? candidate.llm_confidence : 0),
            llm_reasoning: candidate?.llm_reasoning || (isManual ? 'Manually selected by user. LLM validation was skipped.' : ''),
            llm_concerns: Array.isArray(candidate?.llm_concerns) ? candidate.llm_concerns : [],
            _combined_index: typeof candidate?.combinedIndex === 'number'
                ? candidate.combinedIndex
                : (typeof candidate?._combined_index === 'number' ? candidate._combined_index : null),
        };
    };
    const isManualAcceptedCandidate = (candidate) => {
        const normalizedCandidate = normalizeAcceptedCandidate(candidate);
        return String(normalizedCandidate?.match_stage || '').toLowerCase() === 'manual'
            || String(normalizedCandidate?.selection_mode || '').toLowerCase() === 'manual'
            || normalizeMatchPath(normalizedCandidate?.match_path, '') === 'manual';
    };
    const buildManualOverrideTarget = (match, fallbackLayer = '', fallbackPath = '') => {
        if (!match || match?.isPlaceholder || !match?.catalyst_table || !match?.catalyst_column) return null;
        return {
            catalyst_layer: match.catalyst_layer || fallbackLayer,
            catalyst_table: match.catalyst_table,
            catalyst_column: match.catalyst_column,
            catalyst_attribute: match.catalyst_attribute || '',
            catalyst_definition: match.catalyst_definition || '',
            catalyst_data_type: match.catalyst_data_type || '',
            match_path: normalizeMatchPath(match.matchPath || match.match_path, fallbackPath),
            match_stage: match.match_stage || '',
            vector_score: typeof match?.vector_score === 'number' ? match.vector_score : 0,
            llm_confidence: typeof match?.llm_confidence === 'number' ? match.llm_confidence : 0,
            llm_reasoning: match.llm_reasoning || '',
            llm_concerns: Array.isArray(match?.llm_concerns) ? match.llm_concerns : [],
            selection_mode: String(match?.selection_mode || '').toLowerCase() === 'manual' ? 'manual' : 'suggested',
            _combined_index: getCandidateCombinedIndex(match),
        };
    };
    const buildCandidateSelectionPayload = (candidate, fallbackLayer = '', selectionModeOverride = '') => {
        if (!candidate?.catalyst_table || !candidate?.catalyst_column) return null;
        const normalizedCandidate = normalizeAcceptedCandidate(candidate, fallbackLayer);
        const inferredManual = String(normalizedCandidate?.match_stage || '').toLowerCase() === 'manual'
            || String(normalizedCandidate?.match_path || '').toLowerCase() === 'manual'
            || String(candidate?.selection_mode || '').toLowerCase() === 'manual';
        const selectionMode = selectionModeOverride || (inferredManual ? 'manual' : 'suggested');
        const payload = {
            catalyst_layer: normalizedCandidate.catalyst_layer || fallbackLayer,
            catalyst_table: normalizedCandidate.catalyst_table,
            catalyst_column: normalizedCandidate.catalyst_column,
            catalyst_attribute: normalizedCandidate.catalyst_attribute || '',
            catalyst_definition: normalizedCandidate.catalyst_definition || '',
            catalyst_data_type: normalizedCandidate.catalyst_data_type || '',
            match_stage: selectionMode === 'manual' ? (normalizedCandidate.match_stage || 'manual') : (normalizedCandidate.match_stage || ''),
            match_path: selectionMode === 'manual' ? (normalizedCandidate.match_path || 'manual') : (normalizedCandidate.match_path || ''),
            llm_reasoning: normalizedCandidate.llm_reasoning || '',
            llm_confidence: typeof normalizedCandidate.llm_confidence === 'number'
                ? normalizedCandidate.llm_confidence
                : (selectionMode === 'manual' ? 1 : 0),
            llm_concerns: Array.isArray(normalizedCandidate.llm_concerns) ? normalizedCandidate.llm_concerns : [],
            vector_score: typeof normalizedCandidate.vector_score === 'number'
                ? normalizedCandidate.vector_score
                : (selectionMode === 'manual' ? 1 : 0),
            selection_mode: selectionMode,
        };
        if (normalizedCandidate.manual_override_target) {
            payload.manual_override_target = { ...normalizedCandidate.manual_override_target };
        }
        if (Number.isInteger(normalizedCandidate.manual_override_target_index)) {
            payload.manual_override_target_index = normalizedCandidate.manual_override_target_index;
        }
        const manualOverrideMatchPath = normalizeMatchPath(normalizedCandidate.manual_override_match_path, '');
        if (manualOverrideMatchPath) {
            payload.manual_override_match_path = manualOverrideMatchPath;
        }
        return payload;
    };
    const toAcceptedCandidate = (match, layer) => normalizeAcceptedCandidate(match, layer);
    const isSameAcceptedCandidate = (left, right, fallbackLayer = '') => (
        normalizeIdentifier(left?.catalyst_table) === normalizeIdentifier(right?.catalyst_table)
        && normalizeIdentifier(left?.catalyst_column) === normalizeIdentifier(right?.catalyst_column)
        && normalizeIdentifier(left?.catalyst_layer || fallbackLayer) === normalizeIdentifier(right?.catalyst_layer || fallbackLayer)
    );
    const dedupeAcceptedCandidates = (candidates, fallbackLayer = '') => {
        const deduped = [];
        (candidates || []).forEach((candidate) => {
            if (!candidate?.catalyst_table || !candidate?.catalyst_column) return;
            const normalizedCandidate = normalizeAcceptedCandidate(candidate, fallbackLayer);
            if (deduped.some((existing) => isSameAcceptedCandidate(existing, normalizedCandidate, fallbackLayer))) return;
            deduped.push(normalizedCandidate);
        });
        return deduped;
    };
    const getAcceptedCandidates = (decision, fallbackLayer = '') => {
        const candidates = Array.isArray(decision?.accepted_candidates) ? decision.accepted_candidates : [];
        if (candidates.length > 0) {
            return dedupeAcceptedCandidates(candidates, fallbackLayer);
        }
        if (decision?.status === 'accepted' && decision?.catalyst_table && decision?.catalyst_column) {
            return [normalizeAcceptedCandidate({
                catalyst_table: decision.catalyst_table,
                catalyst_column: decision.catalyst_column,
                catalyst_attribute: decision.catalyst_attribute || '',
                catalyst_definition: decision.catalyst_definition || '',
                catalyst_data_type: decision.catalyst_data_type || '',
                catalyst_layer: decision.catalyst_layer || fallbackLayer,
                match_stage: decision.match_stage || '',
                match_path: decision.match_path || (String(decision?.selection_mode || '').toLowerCase() === 'manual' ? 'manual' : ''),
                selection_mode: decision.selection_mode || '',
                vector_score: typeof decision?.vector_confidence === 'number' ? decision.vector_confidence : 0,
                llm_confidence: typeof decision?.llm_confidence === 'number' ? decision.llm_confidence : 0,
                llm_reasoning: decision.llm_reasoning || '',
                llm_concerns: Array.isArray(decision?.llm_concerns) ? decision.llm_concerns : [],
                _combined_index: typeof decision?.selected_match_index === 'number' ? decision.selected_match_index : null,
            }, fallbackLayer)];
        }
        return [];
    };
    const getRejectedCandidates = (decision, fallbackLayer = '') => {
        const candidates = Array.isArray(decision?.rejected_candidates) ? decision.rejected_candidates : [];
        return dedupeAcceptedCandidates(candidates, fallbackLayer);
    };
    const getAcceptedMatchIndexes = (decision, fallbackLayer = '') => {
        const explicitIndexes = Array.isArray(decision?.accepted_match_indexes)
            ? decision.accepted_match_indexes.filter((index) => Number.isInteger(index))
            : [];
        if (explicitIndexes.length > 0) return Array.from(new Set(explicitIndexes));
        const candidateIndexes = getAcceptedCandidates(decision, fallbackLayer)
            .map((candidate) => candidate?._combined_index)
            .filter((index) => Number.isInteger(index));
        if (candidateIndexes.length > 0) return Array.from(new Set(candidateIndexes));
        return Number.isInteger(decision?.selected_match_index) ? [decision.selected_match_index] : [];
    };
    const getPrimaryAcceptedCandidate = (decision, fallbackLayer = '') => {
        const acceptedCandidates = getAcceptedCandidates(decision, fallbackLayer);
        if (acceptedCandidates.length === 0) return null;
        if (Number.isInteger(decision?.selected_match_index)) {
            const matchedCandidate = acceptedCandidates.find(
                (candidate) => candidate?._combined_index === decision.selected_match_index,
            );
            if (matchedCandidate) return matchedCandidate;
        }
        return acceptedCandidates[acceptedCandidates.length - 1];
    };
    const isMatchAccepted = (decision, match, fallbackLayer = '') => {
        if (!match || match?.isPlaceholder) return false;
        const acceptedCandidates = getAcceptedCandidates(decision, fallbackLayer);
        if (acceptedCandidates.length > 0) {
            const matchPath = normalizeMatchPath(match?.matchPath || match?.match_path, '');
            return acceptedCandidates.some((candidate) => {
                const candidatePath = normalizeMatchPath(candidate?.match_path || decision?.match_path, '');
                if (
                    isManualAcceptedCandidate(candidate)
                    && ['exact', 'semantic'].includes(candidatePath)
                    && matchPath
                    && matchPath !== candidatePath
                ) {
                    return false;
                }
                return isSameAcceptedCandidate(candidate, toAcceptedCandidate(match, fallbackLayer), fallbackLayer);
            });
        }
        return Number.isInteger(match?.combinedIndex) && getAcceptedMatchIndexes(decision, fallbackLayer).includes(match.combinedIndex);
    };
    const isMatchRejected = (decision, match, fallbackLayer = '') => {
        if (!match || match?.isPlaceholder) return false;
        const rejectedCandidates = getRejectedCandidates(decision, fallbackLayer);
        if (rejectedCandidates.length > 0) {
            const matchPath = normalizeMatchPath(match?.matchPath || match?.match_path, '');
            return rejectedCandidates.some((candidate) => {
                const candidatePath = normalizeMatchPath(candidate?.match_path || decision?.match_path, '');
                if (
                    isManualAcceptedCandidate(candidate)
                    && ['exact', 'semantic'].includes(candidatePath)
                    && matchPath
                    && matchPath !== candidatePath
                ) {
                    return false;
                }
                return isSameAcceptedCandidate(candidate, toAcceptedCandidate(match, fallbackLayer), fallbackLayer);
            });
        }
        return (
            Number.isInteger(match?.combinedIndex)
            && Array.isArray(decision?.rejected_match_indexes)
            && decision.rejected_match_indexes.includes(match.combinedIndex)
        );
    };
    const getPreferenceMatchStatus = (match) => {
        const status = String(match?.preference_status || '').trim().toLowerCase();
        return ['accepted', 'rejected'].includes(status) ? status : '';
    };
    const hasExplicitLayerDecisionState = (decision, fallbackLayer = '') => {
        if (!decision) return false;
        if (decision?.status === 'accepted' || decision?.status === 'rejected') return true;
        if (getAcceptedCandidates(decision, fallbackLayer).length > 0) return true;
        if (Array.isArray(decision?.accepted_match_indexes) && decision.accepted_match_indexes.length > 0) return true;
        if (Array.isArray(decision?.rejected_match_indexes) && decision.rejected_match_indexes.length > 0) return true;
        if (Array.isArray(decision?.rejected_candidates) && decision.rejected_candidates.length > 0) return true;
        return Boolean(decision?.decided_at || decision?.decided_by || decision?.user_notes);
    };
    const getResolvedMatchDecisionStatus = (decision, match, fallbackLayer = '') => {
        if (!match || match?.isPlaceholder) return 'pending';
        if (isMatchAccepted(decision, match, fallbackLayer)) return 'accepted';
        if (isMatchRejected(decision, match, fallbackLayer)) return 'rejected';
        if (hasExplicitLayerDecisionState(decision, fallbackLayer)) return 'pending';
        return getPreferenceMatchStatus(match) || 'pending';
    };
    const resolveAcceptedBucketPath = (col, layer) => {
        const layerDecision = getLayerDecision(col, layer);
        const acceptedCandidates = getAcceptedCandidates(layerDecision, layer);
        if (acceptedCandidates.length === 0) return null;
        const primaryAcceptedCandidate = getPrimaryAcceptedCandidate(layerDecision, layer);
        const primaryPath = normalizeMatchPath(primaryAcceptedCandidate?.match_path || layerDecision?.match_path, '');
        if (isManualAcceptedCandidate(primaryAcceptedCandidate) && ['exact', 'semantic'].includes(primaryPath)) {
            return primaryPath;
        }
        const exactMatches = getStoredExactMatchesForLayer(col, layer);
        const semanticMatches = getStoredSemanticMatchesForLayer(col, layer);
        const legacyMatches = Array.isArray(col?.matches_by_layer?.[layer]) ? col.matches_by_layer[layer] : [];
        if (primaryAcceptedCandidate) {
            if (exactMatches.some((match) => isSameAcceptedCandidate(primaryAcceptedCandidate, match, layer))) {
                return 'exact';
            }
            if (
                semanticMatches.some((match) => isSameAcceptedCandidate(primaryAcceptedCandidate, match, layer))
                || legacyMatches.some((match) => isSameAcceptedCandidate(primaryAcceptedCandidate, match, layer))
            ) {
                return 'semantic';
            }
        }
        if (acceptedCandidates.some((candidate) => exactMatches.some((match) => isSameAcceptedCandidate(candidate, match, layer)))) {
            return 'exact';
        }
        if (acceptedCandidates.some((candidate) => semanticMatches.some((match) => isSameAcceptedCandidate(candidate, match, layer)))) {
            return 'semantic';
        }
        return 'semantic';
    };
    const findManualOverrideTargetIndex = (matches, manualCandidate, layer, activePath) => {
        if (!Array.isArray(matches) || !manualCandidate) return -1;

        const target = manualCandidate.manual_override_target || null;
        const targetPath = normalizeMatchPath(
            manualCandidate.manual_override_match_path || target?.match_path,
            '',
        );
        if (targetPath && activePath && targetPath !== activePath) return -1;

        const targetCombinedIndex = Number.isInteger(manualCandidate.manual_override_target_index)
            ? manualCandidate.manual_override_target_index
            : (Number.isInteger(target?._combined_index) ? target._combined_index : null);
        if (targetCombinedIndex !== null) {
            const indexByCombinedIndex = matches.findIndex((match) => getCandidateCombinedIndex(match) === targetCombinedIndex);
            if (indexByCombinedIndex >= 0) return indexByCombinedIndex;
        }

        if (target?.catalyst_table && target?.catalyst_column) {
            const indexByIdentity = matches.findIndex((match) => isSameAcceptedCandidate(target, match, layer));
            if (indexByIdentity >= 0) return indexByIdentity;
        }

        return -1;
    };
    const hasManualOverrideTarget = (candidate) => Boolean(
        candidate?.manual_override_target
        || Number.isInteger(candidate?.manual_override_target_index)
    );
    const didManualOverrideReplaceAcceptedMatch = (candidate) => Boolean(
        candidate?.manual_override_target?.was_accepted
    );
    const isSameManualOverrideTarget = (left, right, fallbackLayer = '') => {
        if (!hasManualOverrideTarget(left) || !hasManualOverrideTarget(right)) return false;

        const leftIndex = Number.isInteger(left?.manual_override_target_index)
            ? left.manual_override_target_index
            : (Number.isInteger(left?.manual_override_target?._combined_index) ? left.manual_override_target._combined_index : null);
        const rightIndex = Number.isInteger(right?.manual_override_target_index)
            ? right.manual_override_target_index
            : (Number.isInteger(right?.manual_override_target?._combined_index) ? right.manual_override_target._combined_index : null);
        if (leftIndex !== null && rightIndex !== null) {
            return leftIndex === rightIndex
                && normalizeIdentifier(left?.catalyst_layer || left?.manual_override_target?.catalyst_layer || fallbackLayer)
                    === normalizeIdentifier(right?.catalyst_layer || right?.manual_override_target?.catalyst_layer || fallbackLayer);
        }

        return isSameAcceptedCandidate(left?.manual_override_target, right?.manual_override_target, fallbackLayer)
            && normalizeMatchPath(left?.manual_override_match_path || left?.manual_override_target?.match_path, '')
                === normalizeMatchPath(right?.manual_override_match_path || right?.manual_override_target?.match_path, '');
    };
    const shouldRemoveSelectedCandidate = (candidate, selectedCandidate, layer) => {
        if (!selectedCandidate) return false;
        if (hasManualOverrideTarget(selectedCandidate)) {
            if (isSameManualOverrideTarget(candidate, selectedCandidate, layer)) {
                return true;
            }
            if (isSameAcceptedCandidate(candidate, selectedCandidate?.manual_override_target, layer)) {
                return true;
            }
            const targetIndex = Number.isInteger(selectedCandidate?.manual_override_target_index)
                ? selectedCandidate.manual_override_target_index
                : (Number.isInteger(selectedCandidate?.manual_override_target?._combined_index)
                    ? selectedCandidate.manual_override_target._combined_index
                    : null);
            return targetIndex !== null && getCandidateCombinedIndex(candidate) === targetIndex;
        }
        return isSameAcceptedCandidate(candidate, selectedCandidate, layer);
    };
    const restoreManualOverrideTargetCandidate = (column, selectedCandidate, layer) => {
        if (!selectedCandidate) return null;
        const target = selectedCandidate?.manual_override_target || null;
        const targetIndex = Number.isInteger(selectedCandidate?.manual_override_target_index)
            ? selectedCandidate.manual_override_target_index
            : (Number.isInteger(target?._combined_index) ? target._combined_index : null);
        const allMatches = [
            ...getStoredExactMatchesForLayer(column, layer),
            ...getStoredSemanticMatchesForLayer(column, layer),
            ...(Array.isArray(column?.matches_by_layer?.[layer]) ? column.matches_by_layer[layer] : []),
        ];

        if (targetIndex !== null) {
            const matchByIndex = allMatches.find((match) => getCandidateCombinedIndex(match) === targetIndex);
            if (matchByIndex) return normalizeAcceptedCandidate(matchByIndex, layer);
        }
        if (target?.catalyst_table && target?.catalyst_column) {
            const matchByIdentity = allMatches.find((match) => isSameAcceptedCandidate(match, target, layer));
            if (matchByIdentity) return normalizeAcceptedCandidate(matchByIdentity, layer);
            return normalizeAcceptedCandidate(target, layer);
        }
        return null;
    };

    const buildUpdatedColumnDecisionState = (column, layer, status, selectedMatch = null) => {
        const now = new Date().toISOString();
        const nextLayerDecisions = {
            ...(column?.layer_decisions || {}),
        };
        const existingDecision = nextLayerDecisions[layer] || { status: 'pending', catalyst_layer: layer };
        const existingRejectedIndexes = Array.isArray(existingDecision?.rejected_match_indexes)
            ? existingDecision.rejected_match_indexes
            : [];
        const existingAcceptedCandidates = getAcceptedCandidates(existingDecision, layer);
        const existingRejectedCandidates = getRejectedCandidates(existingDecision, layer);
        const selectedCandidate = selectedMatch ? toAcceptedCandidate(selectedMatch, layer) : null;

        const buildAcceptedLayerDecision = (
            acceptedCandidates,
            rejectedIndexes,
            rejectedCandidates = existingRejectedCandidates,
            baseDecision = existingDecision,
        ) => {
            const normalizedAcceptedCandidates = dedupeAcceptedCandidates(acceptedCandidates, layer);
            const normalizedRejectedCandidates = dedupeAcceptedCandidates(rejectedCandidates, layer);
            const primaryCandidate = normalizedAcceptedCandidates[normalizedAcceptedCandidates.length - 1] || null;
            const acceptedMatchIndexes = normalizedAcceptedCandidates
                .map((candidate) => candidate?._combined_index)
                .filter((index) => Number.isInteger(index));
            const rejectedMatchIndexes = [
                ...normalizedRejectedCandidates
                    .map((candidate) => candidate?._combined_index)
                    .filter((index) => Number.isInteger(index)),
                ...((rejectedIndexes || []).filter((index) => Number.isInteger(index))),
            ];
            return {
                ...baseDecision,
                status: normalizedAcceptedCandidates.length > 0 ? 'accepted' : 'pending',
                selected_match_index: primaryCandidate?._combined_index ?? null,
                accepted_match_indexes: Array.from(new Set(acceptedMatchIndexes)),
                accepted_candidates: normalizedAcceptedCandidates,
                rejected_match_indexes: Array.from(new Set(rejectedMatchIndexes)),
                rejected_candidates: normalizedRejectedCandidates,
                catalyst_table: primaryCandidate?.catalyst_table || '',
                catalyst_column: primaryCandidate?.catalyst_column || '',
                catalyst_attribute: primaryCandidate?.catalyst_attribute || '',
                catalyst_definition: primaryCandidate?.catalyst_definition || '',
                catalyst_data_type: primaryCandidate?.catalyst_data_type || '',
                catalyst_layer: layer,
                match_path: primaryCandidate?.match_path || baseDecision?.match_path || '',
                match_stage: primaryCandidate?.match_stage || baseDecision?.match_stage || column.match_stage,
                vector_confidence: typeof primaryCandidate?.vector_score === 'number' ? primaryCandidate.vector_score : 0,
                llm_confidence: typeof primaryCandidate?.llm_confidence === 'number' ? primaryCandidate.llm_confidence : 0,
                llm_reasoning: primaryCandidate?.llm_reasoning || '',
                llm_concerns: Array.isArray(primaryCandidate?.llm_concerns) ? primaryCandidate.llm_concerns : [],
                decided_at: now,
                decided_by: baseDecision.decided_by || column.decided_by || 'user',
                selection_mode: primaryCandidate
                    ? ((primaryCandidate.match_stage || '').toLowerCase() === 'manual' ? 'manual' : 'suggested')
                    : null,
            };
        };

        if (status === 'accepted' && selectedMatch) {
            nextLayerDecisions[layer] = buildAcceptedLayerDecision(
                [
                    ...existingAcceptedCandidates.filter((candidate) => !shouldRemoveSelectedCandidate(candidate, selectedCandidate, layer)),
                    selectedCandidate,
                ],
                existingRejectedIndexes.filter((index) => index !== selectedMatch.combinedIndex),
                existingRejectedCandidates.filter((candidate) => !shouldRemoveSelectedCandidate(candidate, selectedCandidate, layer)),
            );
        } else if (status === 'pending') {
            const nextRejectedIndexes = selectedMatch
                ? existingRejectedIndexes.filter((index) => index !== selectedMatch.combinedIndex)
                : existingRejectedIndexes;
            const nextAcceptedCandidates = selectedCandidate
                ? existingAcceptedCandidates.filter((candidate) => !shouldRemoveSelectedCandidate(candidate, selectedCandidate, layer))
                : [];
            if (selectedCandidate && isManualAcceptedCandidate(selectedCandidate) && didManualOverrideReplaceAcceptedMatch(selectedCandidate)) {
                const restoredCandidate = restoreManualOverrideTargetCandidate(column, selectedCandidate, layer);
                if (
                    restoredCandidate
                    && !nextAcceptedCandidates.some((candidate) => isSameAcceptedCandidate(candidate, restoredCandidate, layer))
                ) {
                    nextAcceptedCandidates.push(restoredCandidate);
                }
            }
            const nextRejectedCandidates = selectedCandidate
                ? existingRejectedCandidates.filter((candidate) => !shouldRemoveSelectedCandidate(candidate, selectedCandidate, layer))
                : existingRejectedCandidates;
            nextLayerDecisions[layer] = buildAcceptedLayerDecision(nextAcceptedCandidates, nextRejectedIndexes, nextRejectedCandidates);
        } else if (status === 'rejected') {
            const nextAcceptedCandidates = selectedCandidate
                ? existingAcceptedCandidates.filter((candidate) => !shouldRemoveSelectedCandidate(candidate, selectedCandidate, layer))
                : [];
            const nextRejectedCandidates = selectedCandidate
                ? [
                    ...existingRejectedCandidates.filter((candidate) => !shouldRemoveSelectedCandidate(candidate, selectedCandidate, layer)),
                    selectedCandidate,
                ]
                : existingRejectedCandidates;
            nextLayerDecisions[layer] = buildAcceptedLayerDecision(
                nextAcceptedCandidates,
                Array.from(new Set([...(existingRejectedIndexes || []), selectedMatch?.combinedIndex].filter((value) => Number.isInteger(value)))),
                nextRejectedCandidates,
            );
        }

        const acceptedLayers = Object.entries(nextLayerDecisions).filter(
            ([layerKey, decision]) => decision?.status === 'accepted' && getAcceptedCandidates(decision, layerKey).length > 0,
        );

        if (acceptedLayers.length > 0) {
            acceptedLayers.sort((left, right) => {
                const timestampDelta = getDecisionTimestamp(right[1]) - getDecisionTimestamp(left[1]);
                if (timestampDelta !== 0) return timestampDelta;
                return left[0] === 'L3' ? -1 : 1;
            });
            const [selectedLayer, selectedDecision] = acceptedLayers[0];
            const primaryCandidate = getPrimaryAcceptedCandidate(selectedDecision, selectedLayer);
            return {
                ...column,
                status: 'accepted',
                selected_match_index: selectedDecision.selected_match_index,
                layer_decisions: nextLayerDecisions,
                decided_at: selectedDecision.decided_at,
                decided_by: selectedDecision.decided_by,
                catalyst_table: primaryCandidate?.catalyst_table || selectedDecision.catalyst_table,
                catalyst_column: primaryCandidate?.catalyst_column || selectedDecision.catalyst_column,
                catalyst_attribute: primaryCandidate?.catalyst_attribute || selectedDecision.catalyst_attribute,
                catalyst_definition: primaryCandidate?.catalyst_definition || selectedDecision.catalyst_definition,
                catalyst_data_type: primaryCandidate?.catalyst_data_type || selectedDecision.catalyst_data_type,
                catalyst_layer: selectedLayer,
                match_stage: primaryCandidate?.match_stage || selectedDecision.match_stage || column.match_stage,
                vector_confidence: typeof primaryCandidate?.vector_score === 'number' ? primaryCandidate.vector_score : selectedDecision.vector_confidence,
                llm_confidence: typeof primaryCandidate?.llm_confidence === 'number' ? primaryCandidate.llm_confidence : selectedDecision.llm_confidence,
                llm_reasoning: primaryCandidate?.llm_reasoning || selectedDecision.llm_reasoning,
                llm_concerns: Array.isArray(primaryCandidate?.llm_concerns) ? primaryCandidate.llm_concerns : selectedDecision.llm_concerns,
            };
        }

        const nonPendingDecisions = Object.values(nextLayerDecisions).filter((decision) => decision?.status && decision.status !== 'pending');
        const nextStatus = nonPendingDecisions.length > 0 && nonPendingDecisions.every((decision) => decision.status === 'rejected')
            ? 'rejected'
            : 'pending';
        const latestDecision = nonPendingDecisions.reduce((latest, decision) => {
            if (!latest) return decision;
            return getDecisionTimestamp(decision) >= getDecisionTimestamp(latest) ? decision : latest;
        }, null);

        return {
            ...column,
            status: nextStatus,
            selected_match_index: null,
            layer_decisions: nextLayerDecisions,
            decided_at: latestDecision?.decided_at || null,
            decided_by: latestDecision?.decided_by || column.decided_by,
            catalyst_table: '',
            catalyst_column: '',
            catalyst_attribute: '',
            catalyst_definition: '',
            catalyst_data_type: '',
            catalyst_layer: '',
            match_stage: column.match_stage,
            vector_confidence: null,
            llm_confidence: null,
            llm_reasoning: '',
            llm_concerns: [],
        };
    };

    const applyLocalDecisionUpdate = (columnKey, layer, status, selectedMatch = null) => {
        setMappingData((current) => {
            if (!current?.columns) return current;

            let countsDelta = { pending: 0, accepted: 0, rejected: 0 };
            const updatedColumns = current.columns.map((column) => {
                if (column.column_key !== columnKey) return column;
                const updatedColumn = buildUpdatedColumnDecisionState(column, layer, status, selectedMatch);
                if (column.status !== updatedColumn.status) {
                    countsDelta = {
                        ...countsDelta,
                        [column.status]: (countsDelta[column.status] || 0) - 1,
                        [updatedColumn.status]: (countsDelta[updatedColumn.status] || 0) + 1,
                    };
                }
                return updatedColumn;
            });

            return {
                ...current,
                columns: updatedColumns,
                pending_decisions: Math.max(0, (current.pending_decisions || 0) + (countsDelta.pending || 0)),
                accepted_decisions: Math.max(0, (current.accepted_decisions || 0) + (countsDelta.accepted || 0)),
                rejected_decisions: Math.max(0, (current.rejected_decisions || 0) + (countsDelta.rejected || 0)),
            };
        });
    };

    const resetDiagnosticsState = () => {
        setDiagnosticCatalogLoading(false);
        setDiagnosticTargetCatalogLoading(false);
        setDiagnosticFieldLoading(false);
        setDiagnosticReadyFileId(null);
        setDiagnosticSourceTables([]);
        setDiagnosticSourceTable(null);
        setDiagnosticSourceColumn(null);
        setDiagnosticSourceTableInput('');
        setDiagnosticSourceColumnInput('');
        setDiagnosticFieldData(null);
        setDiagnosticTargetLayer('L2');
        setDiagnosticTargetTablesByLayer({ L2: [], L3: [] });
        setDiagnosticTargetTableMetaByLayer({ L2: {}, L3: {} });
        setDiagnosticTargetColumnsByLayer({ L2: {}, L3: {} });
        setDiagnosticTargetColumnsCache({});
        setDiagnosticTargetTable(null);
        setDiagnosticTargetColumn(null);
        setDiagnosticTargetColumns([]);
        setDiagnosticTargetLoadingColumns(false);
        setDiagnosticTargetTableInput('');
        setDiagnosticTargetColumnInput('');
        setDiagnosticSemanticScore(null);
        setDiagnosticSemanticScoreLoading(false);
        setDiagnosticTargetTrace(null);
        setDiagnosticTargetTraceLoading(false);
        setDiagnosticLiveTrace(null);
        setDiagnosticLiveTraceLoading(false);
        setDiagnosticLiveReplayIndex(0);
        setDiagnosticLivePipelineRunning(false);
        setDiagnosticLiveStatusMessage('');
        setDiagnosticLiveProgress({ current: null, total: null, percent: null });
        setDiagnosticLiveExpandedPanel('exact_lookup');
        setDiagnosticLiveRunId(null);
    };

    const buildDiagnosticTableMeta = (tables) => {
        const tableMeta = {};

        (tables || []).forEach((table) => {
            if (!table?.table_name) return;
            tableMeta[normalizeIdentifier(table.table_name)] = table;
        });

        return tableMeta;
    };

    const loadDiagnosticCatalog = useCallback(async (sourceSystem, fileId) => {
        try {
            setDiagnosticCatalogLoading(true);
            setDiagnosticTargetCatalogLoading(true);
            const catalog = await getMappingDiagnosticsCatalog(projectId, sourceSystem, fileId);
            const sourceTables = Array.isArray(catalog?.source_tables) ? catalog.source_tables : [];
            const targetTablesByLayer = {
                L2: Array.isArray(catalog?.target_tables_by_layer?.L2) ? catalog.target_tables_by_layer.L2 : [],
                L3: Array.isArray(catalog?.target_tables_by_layer?.L3) ? catalog.target_tables_by_layer.L3 : [],
            };
            const targetColumnsByLayer = {
                L2: catalog?.target_columns_by_layer?.L2 && typeof catalog.target_columns_by_layer.L2 === 'object'
                    ? catalog.target_columns_by_layer.L2
                    : {},
                L3: catalog?.target_columns_by_layer?.L3 && typeof catalog.target_columns_by_layer.L3 === 'object'
                    ? catalog.target_columns_by_layer.L3
                    : {},
            };

            setDiagnosticSourceTables(sourceTables);
            setDiagnosticTargetTablesByLayer(targetTablesByLayer);
            setDiagnosticTargetTableMetaByLayer({
                L2: buildDiagnosticTableMeta(targetTablesByLayer.L2),
                L3: buildDiagnosticTableMeta(targetTablesByLayer.L3),
            });
            setDiagnosticTargetColumnsByLayer(targetColumnsByLayer);
            setDiagnosticTargetColumnsCache({});
            setDiagnosticReadyFileId(fileId);
            setError((prev) => (prev === 'Failed to load diagnostics catalog.' ? null : prev));

            const sourceTableStillValid = sourceTables.some(
                (table) => normalizeIdentifier(table?.table_name) === normalizeIdentifier(diagnosticSourceTable?.table_name),
            );
            if (!sourceTableStillValid) {
                setDiagnosticSourceTable(null);
                setDiagnosticSourceColumn(null);
                setDiagnosticSourceTableInput('');
                setDiagnosticSourceColumnInput('');
                setDiagnosticFieldData(null);
            } else {
                const nextSourceTable = sourceTables.find(
                    (table) => normalizeIdentifier(table?.table_name) === normalizeIdentifier(diagnosticSourceTable?.table_name),
                );
                const sourceColumnStillValid = (nextSourceTable?.columns || []).some(
                    (column) => column?.column_key === diagnosticSourceColumn?.column_key,
                );
                setDiagnosticSourceTable(nextSourceTable || null);
                if (!sourceColumnStillValid) {
                    setDiagnosticSourceColumn(null);
                    setDiagnosticSourceColumnInput('');
                    setDiagnosticFieldData(null);
                } else {
                    const nextSourceColumn = (nextSourceTable?.columns || []).find(
                        (column) => column?.column_key === diagnosticSourceColumn?.column_key,
                    );
                    setDiagnosticSourceColumn(nextSourceColumn || null);
                }
            }

            const validTargetTables = targetTablesByLayer[diagnosticTargetLayer] || [];
            const targetTableStillValid = validTargetTables.some(
                (table) => normalizeIdentifier(table?.table_name) === normalizeIdentifier(diagnosticTargetTable?.table_name),
            );
            if (!targetTableStillValid) {
                setDiagnosticTargetTable(null);
                setDiagnosticTargetColumn(null);
                setDiagnosticTargetColumns([]);
                setDiagnosticTargetTableInput('');
                setDiagnosticTargetColumnInput('');
                setDiagnosticSemanticScore(null);
                setDiagnosticTargetTrace(null);
            } else {
                const nextTargetTable = validTargetTables.find(
                    (table) => normalizeIdentifier(table?.table_name) === normalizeIdentifier(diagnosticTargetTable?.table_name),
                );
                setDiagnosticTargetTable(nextTargetTable || null);
                const targetTableKey = normalizeIdentifier(nextTargetTable?.table_name);
                const nextTargetColumns = Array.isArray(targetColumnsByLayer?.[diagnosticTargetLayer]?.[targetTableKey])
                    ? targetColumnsByLayer[diagnosticTargetLayer][targetTableKey]
                    : [];
                setDiagnosticTargetColumns(nextTargetColumns);
                const targetColumnStillValid = nextTargetColumns.some(
                    (column) => normalizeIdentifier(column?.column_name) === normalizeIdentifier(diagnosticTargetColumn?.column_name),
                );
                if (!targetColumnStillValid) {
                    setDiagnosticTargetColumn(null);
                    setDiagnosticTargetColumnInput('');
                    setDiagnosticSemanticScore(null);
                    setDiagnosticTargetTrace(null);
                }
            }
        } catch (err) {
            setError('Failed to load diagnostics catalog.');
        } finally {
            setDiagnosticCatalogLoading(false);
            setDiagnosticTargetCatalogLoading(false);
        }
    }, [projectId, diagnosticSourceTable, diagnosticSourceColumn, diagnosticTargetLayer, diagnosticTargetTable, diagnosticTargetColumn]);

    const stopStatusPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    useEffect(() => (
        () => {
            stopStatusPolling();
        }
    ), [stopStatusPolling]);

    useEffect(() => {
        stopStatusPolling();
        setProject(null);
        setFiles({});
        setSelectedFile(null);
        setMappingData(null);
        setProcessing(false);
        setProcessingStatus(null);
        setBatchTimingDialogOpen(false);
        setError(null);
        setActiveView('results');
        setPage(1);
        setTableFilter('');
        setColumnFilter('');
        setStatusFilter('');
        setMatchStageFilter('');
        setRowLayerFilter({});
        setRowMatchPathFilter({});
        setRowVisibleMatchCounts({});
        preferredSelectionAppliedRef.current = false;
        resetDiagnosticsState();
        fetchProjectData();
    }, [projectId, stopStatusPolling]);

    const fetchProjectData = async () => {
        try {
            setLoading(true);
            const [projectData, filesData] = await Promise.all([
                getProject(projectId),
                getProjectFiles(projectId),
            ]);
            setProject(projectData);
            setFiles(filesData.files || {});
            setError(null);
        } catch (err) {
            setError('Failed to load project.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectFile = async (sourceSystem, file) => {
        stopStatusPolling();
        setProcessing(false);
        setProcessingStatus(null);
        setBatchTimingDialogOpen(false);
        setSelectedFile({ sourceSystem, ...file });
        setMappingData(null);
        setActiveView('results');
        setPage(1);
        setTableFilter('');
        setColumnFilter('');
        setStatusFilter('');
        setMatchStageFilter('');
        setRowMatchPathFilter({});
        setRowVisibleMatchCounts({});
        resetDiagnosticsState();
        previewVersionRef.current = 0;

        try {
            const status = await getMappingProcessingStatus(projectId, sourceSystem, file.id);
            if (status.status === 'processing') {
                setProcessing(true);
                setProcessingStatus(status);
                previewVersionRef.current = Number(status.preview_version || 0);
            } else if (status.status === 'interrupted') {
                setProcessing(false);
                setProcessingStatus(status);
                setError(status.error || status.message || 'Processing was interrupted. Start it again.');
            } else {
                setProcessing(false);
                setProcessingStatus(status);
            }
        } catch (err) {
            setProcessing(false);
            setProcessingStatus(null);
        }
    };

    useEffect(() => {
        const preferredFile = location.state?.preferredFile;
        if (!preferredFile || preferredSelectionAppliedRef.current || selectedFile) {
            return;
        }

        const matchedFile = (files[preferredFile.sourceSystem] || []).find(
            (file) => file.id === preferredFile.fileId
        );

        if (!matchedFile) {
            return;
        }

        preferredSelectionAppliedRef.current = true;
        handleSelectFile(preferredFile.sourceSystem, matchedFile);
    }, [files, handleSelectFile, location.state, selectedFile]);

    const loadMappingResults = async (sourceSystem, fileId, pageNum = 1) => {
        const requestId = resultsRequestRef.current + 1;
        resultsRequestRef.current = requestId;
        try {
            const summary = await getMappingResultsPaginated(projectId, sourceSystem, fileId, {
                page: pageNum,
                pageSize,
                tableFilter: tableFilter || undefined,
                columnFilter: columnFilter || undefined,
                statusFilter: statusFilter || undefined,
                matchStageFilter: matchStageFilter || undefined,
            });
            const columnKeys = Array.isArray(summary?.columns)
                ? summary.columns.map((column) => column?.column_key).filter(Boolean)
                : [];

            let mergedColumns = Array.isArray(summary?.columns) ? summary.columns : [];
            if (columnKeys.length > 0) {
                try {
                    const detailsResponse = await getMappingResultsDetails(projectId, sourceSystem, fileId, columnKeys);
                    const detailsByKey = new Map(
                        (detailsResponse?.columns || []).map((column) => [column.column_key, column]),
                    );
                    mergedColumns = mergedColumns.map((column) => ({
                        ...column,
                        ...(detailsByKey.get(column.column_key) || {}),
                    }));
                } catch (detailErr) {
                    // Fall back to lightweight summaries if details fail.
                }
            }

            if (resultsRequestRef.current === requestId) {
                setMappingData({
                    ...summary,
                    columns: mergedColumns,
                });
            }
        } catch (err) {
            if (resultsRequestRef.current === requestId) {
                setMappingData(null);
            }
        }
    };

    useEffect(() => {
        if (selectedFile) {
            loadMappingResults(selectedFile.sourceSystem, selectedFile.id, page);
        }
    }, [selectedFile, page, tableFilter, columnFilter, statusFilter, matchStageFilter]);

    useEffect(() => {
        if (!selectedFile || activeView !== 'pipeline' || !mappingData) {
            return;
        }
        if (diagnosticCatalogLoading || diagnosticReadyFileId === selectedFile.id) {
            return;
        }
        loadDiagnosticCatalog(selectedFile.sourceSystem, selectedFile.id);
    }, [activeView, selectedFile, mappingData, diagnosticCatalogLoading, diagnosticReadyFileId, loadDiagnosticCatalog]);

    useEffect(() => {
        if (
            activeView !== 'pipeline'
            || !selectedFile
            || diagnosticReadyFileId !== selectedFile.id
            || !diagnosticSourceColumn?.column_key
            || !diagnosticTargetLayer
            || !diagnosticTargetTable?.table_name
            || !diagnosticTargetColumn?.column_name
        ) {
            setDiagnosticLiveTrace(null);
            setDiagnosticLiveTraceLoading(false);
            setDiagnosticLiveReplayIndex(0);
            setDiagnosticLivePipelineRunning(false);
            setDiagnosticLiveStatusMessage('');
            setDiagnosticLiveProgress({ current: null, total: null, percent: null });
            setDiagnosticLiveExpandedPanel('exact_lookup');
            setDiagnosticLiveRunId(null);
            return;
        }

        setDiagnosticLiveTrace(null);
        setDiagnosticLiveReplayIndex(0);
        setDiagnosticLivePipelineRunning(false);
        setDiagnosticLiveStatusMessage('');
        setDiagnosticLiveProgress({ current: null, total: null, percent: null });
        setDiagnosticLiveExpandedPanel('exact_lookup');
        setDiagnosticLiveRunId(null);
    }, [
        activeView,
        selectedFile,
        diagnosticReadyFileId,
        diagnosticSourceColumn,
        diagnosticTargetLayer,
        diagnosticTargetTable,
        diagnosticTargetColumn,
    ]);

    useEffect(() => {
        if (
            activeView !== 'pipeline'
            || !diagnosticLivePipelineRunning
            || !diagnosticLiveRunId
            || !selectedFile
            || !diagnosticSourceColumn?.column_key
            || !diagnosticTargetTable?.table_name
            || !diagnosticTargetColumn?.column_name
        ) {
            return undefined;
        }

        let cancelled = false;
        const pollProgress = async () => {
            try {
                const progress = await getMappingDiagnosticLiveTraceProgress(
                    projectId,
                    selectedFile.sourceSystem,
                    selectedFile.id,
                    diagnosticSourceColumn.column_key,
                    diagnosticTargetLayer,
                    diagnosticTargetTable.table_name,
                    diagnosticTargetColumn.column_name,
                    diagnosticLiveRunId,
                );
                if (cancelled) return;
                setDiagnosticLiveStatusMessage(progress?.message || '');
                setDiagnosticLiveProgress({
                    current: Number.isInteger(progress?.progress_current) ? progress.progress_current : null,
                    total: Number.isInteger(progress?.progress_total) ? progress.progress_total : null,
                    percent: typeof progress?.progress_percent === 'number' ? progress.progress_percent : null,
                });
                if (progress?.trace) {
                    setDiagnosticLiveTrace(progress.trace);
                    setDiagnosticLiveTraceLoading(false);
                    const playbackSteps = Array.isArray(progress.trace?.playback_steps) ? progress.trace.playback_steps : [];
                    const stepIndex = playbackSteps.findIndex((step) => step.key === progress?.current_step_key);
                    setDiagnosticLiveReplayIndex(stepIndex >= 0 ? stepIndex : 0);
                }
                if (progress?.status === 'completed' || progress?.status === 'failed') {
                    setDiagnosticLivePipelineRunning(false);
                    setDiagnosticLiveTraceLoading(false);
                    if (progress?.status === 'failed') {
                        setError(progress?.error || progress?.message || 'Live pipeline replay failed.');
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setDiagnosticLivePipelineRunning(false);
                    setDiagnosticLiveTraceLoading(false);
                    setError(err?.response?.data?.detail || err?.message || 'Failed to poll live pipeline trace.');
                }
            }
        };

        pollProgress();
        const timer = window.setInterval(pollProgress, 1500);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [
        activeView,
        diagnosticLivePipelineRunning,
        diagnosticLiveRunId,
        projectId,
        selectedFile,
        diagnosticSourceColumn,
        diagnosticTargetLayer,
        diagnosticTargetTable,
        diagnosticTargetColumn,
    ]);

    useEffect(() => {
        if (activeView !== 'pipeline') return;
        const playbackSteps = Array.isArray(diagnosticLiveTrace?.playback_steps) ? diagnosticLiveTrace.playback_steps : [];
        const activePlaybackStep = playbackSteps[diagnosticLiveReplayIndex];
        if (activePlaybackStep?.key) {
            setDiagnosticLiveExpandedPanel(activePlaybackStep.key);
        }
    }, [activeView, diagnosticLiveTrace, diagnosticLiveReplayIndex]);

    useEffect(() => {
        if (!processing || !selectedFile) {
            stopStatusPolling();
            return undefined;
        }

        const pollStatus = async () => {
            try {
                const status = await getMappingProcessingStatus(projectId, selectedFile.sourceSystem, selectedFile.id);
                setProcessingStatus(status);

                const previewVersion = Number(status.preview_version || 0);
                if (
                    status.partial_results_available &&
                    previewVersion > previewVersionRef.current
                ) {
                    previewVersionRef.current = previewVersion;
                    await loadMappingResults(selectedFile.sourceSystem, selectedFile.id, page);
                }

                if (status.status === 'completed') {
                    stopStatusPolling();
                    await loadMappingResults(selectedFile.sourceSystem, selectedFile.id, 1);
                    if (activeView === 'pipeline') {
                        await loadDiagnosticCatalog(selectedFile.sourceSystem, selectedFile.id);
                    }
                    setPage(1);
                    setProcessing(false);
                    setProcessingStatus(status);
                    setError(null);
                    return;
                }

                if (status.status === 'failed') {
                    stopStatusPolling();
                    setProcessing(false);
                    setProcessingStatus(status);
                    setError(status.error || status.message || 'Processing failed.');
                    return;
                }

                if (status.status === 'interrupted') {
                    stopStatusPolling();
                    setProcessing(false);
                    setProcessingStatus(status);
                    setError(status.error || status.message || 'Processing was interrupted. Start it again.');
                }
            } catch (err) {
                // Keep polling unless the job explicitly fails.
            }
        };

        pollStatus();
        pollingRef.current = setInterval(pollStatus, 2000);

        return () => {
            stopStatusPolling();
        };
    }, [processing, selectedFile, projectId, page, stopStatusPolling, tableFilter, columnFilter, statusFilter, matchStageFilter, activeView, loadDiagnosticCatalog]);

    const handleProcessFile = async () => {
        if (!selectedFile) return;

        stopStatusPolling();
        previewVersionRef.current = 0;
        setError(null);
        setProcessing(true);
        setBatchTimingDialogOpen(false);
        resetDiagnosticsState();
        setProcessingStatus({
            status: 'processing',
            progress: 0,
            phase: 'initializing',
            message: 'Starting mapping...',
        });

        try {
            const response = await processLegacyFile(projectId, selectedFile.sourceSystem, selectedFile.id);
            setProcessingStatus((prev) => ({
                ...(prev || {}),
                status: 'processing',
                progress: Number(prev?.progress || 0),
                phase: prev?.phase || 'initializing',
                total_columns: response?.total_columns || selectedFile.column_count || 0,
                message: response?.message || 'Mapping started.',
            }));
        } catch (err) {
            stopStatusPolling();
            setProcessing(false);
            setProcessingStatus(null);
            setError(err.response?.data?.detail || 'Processing failed.');
        }
    };

    const handleDeleteMappings = async () => {
        if (!selectedFile || !window.confirm('Delete all mapping results for this file?')) return;

        try {
            stopStatusPolling();
            await deleteMappings(projectId, selectedFile.sourceSystem, selectedFile.id);
            setMappingData(null);
            setProcessing(false);
            setProcessingStatus(null);
            setError(null);
            resetDiagnosticsState();
            previewVersionRef.current = 0;
        } catch (err) {
            setError('Failed to delete mappings.');
        }
    };

    const handleDecision = async (column, layer, match, status) => {
        if (!selectedFile) return;

        try {
            const payload = {
                project_id: projectId,
                source_system: selectedFile.sourceSystem,
                file_id: selectedFile.id,
                column_key: column.column_key,
                layer,
                status,
            };

            const candidateSelection = buildCandidateSelectionPayload(match, layer);
            if (candidateSelection) {
                payload.candidate_selection = candidateSelection;
            } else if (Number.isInteger(match?.combinedIndex)) {
                payload.selected_match_index = match?.combinedIndex;
            }

            await saveMappingDecision(payload);
            applyLocalDecisionUpdate(column.column_key, layer, status, match || null);
            setDiagnosticReadyFileId(null);
        } catch (err) {
            setError('Failed to save decision.');
        }
    };

    const openManualSelectionDialog = async (col, layer, matchPath = 'semantic', sourceMatch = null) => {
        const sourceSystem = CATALYST_LAYER_SYSTEMS[layer];
        if (!sourceSystem) return;

        setManualDialogColumn(col);
        setManualDialogLayer(layer);
        setManualDialogPath(normalizeMatchPath(matchPath, 'semantic'));
        setManualDialogSourceMatch(sourceMatch && !sourceMatch?.isPlaceholder ? sourceMatch : null);
        setManualTable(null);
        setManualColumn(null);
        setManualColumns([]);
        setManualDialogOpen(true);
        setManualLoadingTables(true);

        try {
            const response = await getTablesBySource(projectId, sourceSystem);
            const uniqueTables = [];
            const seenTables = new Set();

            (response?.tables || []).forEach((table) => {
                const tableName = String(table?.table_name || '').trim();
                if (!tableName || seenTables.has(tableName.toLowerCase())) return;
                seenTables.add(tableName.toLowerCase());
                uniqueTables.push(table);
            });

            uniqueTables.sort((left, right) => String(left.table_name || '').localeCompare(String(right.table_name || '')));
            setManualTables(uniqueTables);
        } catch (err) {
            setError(`Failed to load ${layer} tables.`);
        } finally {
            setManualLoadingTables(false);
        }
    };

    const handleManualTableChange = async (tableOption, layer) => {
        const sourceSystem = CATALYST_LAYER_SYSTEMS[layer];
        setManualTable(tableOption);
        setManualColumn(null);
        setManualColumns([]);

        if (!tableOption?.table_name || !sourceSystem) {
            return;
        }

        setManualLoadingColumns(true);
        try {
            const normalizedTableName = String(tableOption.table_name || '').trim().toLowerCase();
            let response = await getColumnsBySource(projectId, sourceSystem, tableOption.table_name);
            let responseColumns = Array.isArray(response?.columns) ? response.columns : [];

            if (responseColumns.length === 0 && normalizedTableName) {
                const fallbackResponse = await getColumnsBySource(projectId, sourceSystem);
                responseColumns = (Array.isArray(fallbackResponse?.columns) ? fallbackResponse.columns : []).filter(
                    (column) => String(column?.table_name || '').trim().toLowerCase() === normalizedTableName
                );
            }

            const uniqueColumns = [];
            const seenColumns = new Set();

            responseColumns.forEach((column) => {
                const key = `${String(column?.table_name || '').toLowerCase()}|${String(column?.column_name || '').toLowerCase()}`;
                if (!column?.column_name || seenColumns.has(key)) return;
                seenColumns.add(key);
                uniqueColumns.push(column);
            });

            uniqueColumns.sort((left, right) => String(left.column_name || '').localeCompare(String(right.column_name || '')));
            setManualColumns(uniqueColumns);
        } catch (err) {
            setError(`Failed to load ${layer} columns.`);
        } finally {
            setManualLoadingColumns(false);
        }
    };

    const handleSaveManualSelection = async () => {
        if (!selectedFile || !manualDialogColumn || !manualTable || !manualColumn) return;

        try {
            setManualSaving(true);
            const manualOverrideTarget = buildManualOverrideTarget(
                manualDialogSourceMatch,
                manualDialogLayer,
                manualDialogPath,
            );
            if (manualOverrideTarget) {
                manualOverrideTarget.was_accepted = Boolean(
                    manualDialogSourceMatch
                    && isMatchAccepted(
                        getLayerDecision(manualDialogColumn, manualDialogLayer),
                        manualDialogSourceMatch,
                        manualDialogLayer,
                    )
                );
            }
            const manualSelectedMatch = {
                combinedIndex: null,
                catalyst_layer: manualDialogLayer,
                catalyst_table: manualTable.table_name,
                catalyst_column: manualColumn.column_name,
                catalyst_attribute: manualColumn.attribute_name,
                catalyst_definition: manualColumn.definition,
                catalyst_data_type: manualColumn.data_type,
                match_stage: 'manual',
                match_path: normalizeMatchPath(manualDialogPath, 'semantic'),
                llm_reasoning: 'Manually selected by user. LLM validation was skipped.',
                llm_concerns: [],
                llm_confidence: 1,
                vector_score: 1,
                manual_override_target: manualOverrideTarget,
                manual_override_target_index: manualOverrideTarget?._combined_index ?? null,
                manual_override_match_path: manualOverrideTarget?.match_path || normalizeMatchPath(manualDialogPath, 'semantic'),
            };
            await saveMappingDecision({
                project_id: projectId,
                source_system: selectedFile.sourceSystem,
                file_id: selectedFile.id,
                column_key: manualDialogColumn.column_key,
                layer: manualDialogLayer,
                status: 'accepted',
                candidate_selection: buildCandidateSelectionPayload(manualSelectedMatch, manualDialogLayer, 'manual'),
            });
            applyLocalDecisionUpdate(manualDialogColumn.column_key, manualDialogLayer, 'accepted', manualSelectedMatch);
            setManualDialogOpen(false);
            setManualDialogColumn(null);
            setManualDialogPath('semantic');
            setManualDialogSourceMatch(null);
            setManualTable(null);
            setManualColumn(null);
            setManualTables([]);
            setManualColumns([]);
            setDiagnosticReadyFileId(null);
        } catch (err) {
            setError('Failed to save manual mapping selection.');
        } finally {
            setManualSaving(false);
        }
    };

    const handleDownload = async () => {
        if (!selectedFile) return;

        try {
            setDownloading(true);
            const url = buildMappingExportCsvUrl(projectId, selectedFile.sourceSystem, selectedFile.id, {
                tableFilter,
                columnFilter,
                statusFilter,
                matchStageFilter,
            });
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `schema_mapping_${selectedFile.file_name}_full.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Failed to download mapping results.');
        } finally {
            setDownloading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 0.9) return 'success';
        if (score >= 0.7) return 'warning';
        return 'error';
    };

    const formatElapsedTime = (seconds) => {
        if (!seconds) return '0s';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    };

    const formatDurationMs = (durationMs) => {
        const normalized = Number(durationMs || 0);
        if (!Number.isFinite(normalized) || normalized <= 0) return '0s';
        if (normalized < 1000) return `${normalized}ms`;
        return formatElapsedTime(Math.round(normalized / 1000));
    };

    const showReprocessLabel = Boolean(
        mappingData
        || processingStatus?.status === 'interrupted'
        || processingStatus?.partial_results_available,
    );

    const getProgressPhaseLabel = (phase) => {
        switch (phase) {
            case 'candidate_discovery':
            case 'candidate_retrieval':
                return 'Candidate Retrieval';
            case 'stage_llm_validation':
                return 'Stage Validation';
            case 'complete':
            case 'completed':
                return 'Complete';
            case 'failed':
                return 'Failed';
            case 'interrupted':
                return 'Interrupted';
            case 'initializing':
                return 'Initializing';
            default:
                return phase || 'Processing';
        }
    };

    const getLayerDecision = (col, layer) => col?.layer_decisions?.[layer] || { status: 'pending', catalyst_layer: layer };

    const getMatchSortScore = (match) => {
        if (String(match?.match_stage || '').toLowerCase() === 'manual' || String(match?.match_path || '').toLowerCase() === 'manual') {
            return 1;
        }
        if (typeof match?.llm_confidence === 'number') return match.llm_confidence;
        if (typeof match?.vector_score === 'number') return match.vector_score;
        return 0;
    };
    const getDisplayMatchConfidence = (match) => getMatchSortScore(match);
    const getMatchStageDisplayRank = (match) => {
        const stage = String(match?.match_stage || '').toLowerCase();
        if (stage === 'manual') return -1;
        if (stage === 'same_theme_priority') return 0;
        if (stage === 'other_priority_tables') return 1;
        if (stage === 'same_theme_all') return 2;
        if (stage === 'combined_theme_all') return 3;
        if (stage === 'other_theme_all') return 4;
        if (stage === 'all_tables') return 5;
        if (stage === 'combined_theme_priority') return 6;
        if (stage === 'other_theme_priority') return 7;
        if (stage === 'exact') return 10;
        if (stage === 'column_similarity') return 11;
        if (stage === 'definition_similarity') return 12;
        if (stage === 'fallback') return 13;
        return 20;
    };
    const getPriorityTableRankForMatch = (col, layer, match) => {
        const tableName = String(match?.catalyst_table || '').trim().toLowerCase();
        const priorityRanks = col?.layer_selection_meta?.[layer]?.priority_table_ranks || {};
        if (tableName && Number.isFinite(Number(priorityRanks?.[tableName]))) {
            return Number(priorityRanks[tableName]);
        }
        return Number.MAX_SAFE_INTEGER;
    };
    const compareMatchesForDisplay = (col, layer, left, right) => {
        const leftLayer = String(left?.catalyst_layer || layer || 'L2').toUpperCase();
        const rightLayer = String(right?.catalyst_layer || layer || 'L2').toUpperCase();
        const leftIsManual = (
            String(left?.match_stage || '').toLowerCase() === 'manual'
            || String(left?.match_path || '').toLowerCase() === 'manual'
        ) ? 0 : 1;
        const rightIsManual = (
            String(right?.match_stage || '').toLowerCase() === 'manual'
            || String(right?.match_path || '').toLowerCase() === 'manual'
        ) ? 0 : 1;
        if (leftIsManual !== rightIsManual) return leftIsManual - rightIsManual;

        const leftStageRank = getMatchStageDisplayRank(left);
        const rightStageRank = getMatchStageDisplayRank(right);
        if (leftStageRank !== rightStageRank) return leftStageRank - rightStageRank;

        const leftPriorityRank = getPriorityTableRankForMatch(col, leftLayer, left);
        const rightPriorityRank = getPriorityTableRankForMatch(col, rightLayer, right);
        if (leftPriorityRank !== rightPriorityRank) return leftPriorityRank - rightPriorityRank;

        const leftVectorScore = Number(left?.vector_score || 0);
        const rightVectorScore = Number(right?.vector_score || 0);
        if (leftVectorScore !== rightVectorScore) return rightVectorScore - leftVectorScore;

        const leftLlmConfidence = Number(left?.llm_confidence || 0);
        const rightLlmConfidence = Number(right?.llm_confidence || 0);
        if (leftLlmConfidence !== rightLlmConfidence) return rightLlmConfidence - leftLlmConfidence;

        const tableComparison = String(left?.catalyst_table || '').localeCompare(String(right?.catalyst_table || ''), undefined, { sensitivity: 'base' });
        if (tableComparison !== 0) return tableComparison;

        const columnComparison = String(left?.catalyst_column || '').localeCompare(String(right?.catalyst_column || ''), undefined, { sensitivity: 'base' });
        if (columnComparison !== 0) return columnComparison;

        const leftPosition = Number.isFinite(Number(left?.position)) ? Number(left.position) : Number.MAX_SAFE_INTEGER;
        const rightPosition = Number.isFinite(Number(right?.position)) ? Number(right.position) : Number.MAX_SAFE_INTEGER;
        if (leftPosition !== rightPosition) return leftPosition - rightPosition;

        const leftCombinedIndex = Number.isFinite(Number(left?._combined_index)) ? Number(left._combined_index) : Number.MAX_SAFE_INTEGER;
        const rightCombinedIndex = Number.isFinite(Number(right?._combined_index)) ? Number(right._combined_index) : Number.MAX_SAFE_INTEGER;
        return leftCombinedIndex - rightCombinedIndex;
    };

    const getDefaultMatchPathForColumn = (col, layer) => {
        const acceptedPath = resolveAcceptedBucketPath(col, layer);
        if (acceptedPath) {
            return acceptedPath;
        }
        const exactMatches = col?.exact_matches_by_layer?.[layer];
        if (Array.isArray(exactMatches) && exactMatches.length > 0) {
            return 'exact';
        }
        return 'semantic';
    };

    const getDisplayLimit = () => Number(mappingData?.top_k_to_display || 0);
    const getRowLoadMoreSize = () => {
        const configured = Number(mappingData?.mapping_row_load_more_size || 0);
        return configured > 0 ? configured : Math.max(1, getDisplayLimit() || 1);
    };
    const getRowMatchVisibilityKey = (columnKey, layer, path) => `${columnKey}:${layer}:${path}`;
    const getStoredExactMatchesForLayer = (col, layer) => {
        const allExactMatches = Array.isArray(col?.all_exact_matches_by_layer?.[layer]) ? col.all_exact_matches_by_layer[layer] : null;
        if (Array.isArray(allExactMatches) && allExactMatches.length > 0) {
            return allExactMatches;
        }
        return Array.isArray(col?.exact_matches_by_layer?.[layer]) ? col.exact_matches_by_layer[layer] : [];
    };
    const getStoredSemanticMatchesForLayer = (col, layer) => {
        const allSemanticMatches = Array.isArray(col?.all_semantic_matches_by_layer?.[layer]) ? col.all_semantic_matches_by_layer[layer] : null;
        if (Array.isArray(allSemanticMatches) && allSemanticMatches.length > 0) {
            return allSemanticMatches;
        }
        return Array.isArray(col?.semantic_matches_by_layer?.[layer]) ? col.semantic_matches_by_layer[layer] : [];
    };

    const getAllMatchesForPath = (col, layer, path) => {
        const exactMatches = getStoredExactMatchesForLayer(col, layer);
        const semanticMatches = getStoredSemanticMatchesForLayer(col, layer);
        const legacyMatches = Array.isArray(col?.matches_by_layer?.[layer]) ? col.matches_by_layer[layer] : [];

        if (path === 'exact') {
            return exactMatches;
        }
        if (path === 'semantic') {
            if (semanticMatches.length > 0) {
                return semanticMatches;
            }
            if (exactMatches.length === 0 && semanticMatches.length === 0 && legacyMatches.length > 0) {
                return legacyMatches;
            }
            return semanticMatches;
        }
        return legacyMatches;
    };

    const getVisibleMatchesForPath = (col, layer, path) => {
        const allMatches = [...getAllMatchesForPath(col, layer, path)];
        const layerDecision = getLayerDecision(col, layer);
        const acceptedCandidates = getAcceptedCandidates(layerDecision, layer);
        const exactMatches = getStoredExactMatchesForLayer(col, layer);
        const semanticMatches = getStoredSemanticMatchesForLayer(col, layer);
        const legacyMatches = Array.isArray(col?.matches_by_layer?.[layer]) ? col.matches_by_layer[layer] : [];
        const pinnedAcceptedCandidates = acceptedCandidates
            .filter((candidate) => {
                const candidatePath = normalizeMatchPath(candidate?.match_path || layerDecision?.match_path, '');
                if (candidatePath && candidatePath !== 'manual') {
                    return candidatePath === path;
                }
                if (path === 'exact') {
                    return exactMatches.some((match) => isSameAcceptedCandidate(candidate, match, layer));
                }
                if (path === 'semantic') {
                    return (
                        semanticMatches.some((match) => isSameAcceptedCandidate(candidate, match, layer))
                        || legacyMatches.some((match) => isSameAcceptedCandidate(candidate, match, layer))
                        || candidatePath === 'manual'
                    );
                }
                return true;
            })
            .map((candidate) => ({
                ...normalizeAcceptedCandidate(candidate, layer),
                layer,
                matchPath: path,
            }));

        pinnedAcceptedCandidates.forEach((candidate) => {
            if (!allMatches.some((existing) => isSameAcceptedCandidate(existing, candidate, layer))) {
                allMatches.push(candidate);
            }
        });

        const visibilityKey = getRowMatchVisibilityKey(col.column_key, layer, path);
        const hasCustomVisibleCount = Object.prototype.hasOwnProperty.call(rowVisibleMatchCounts, visibilityKey);
        const configuredVisibleCount = Number(rowVisibleMatchCounts[visibilityKey] || 0);
        const displayLimit = getDisplayLimit();
        const baseVisibleCount = configuredVisibleCount > 0 ? configuredVisibleCount : displayLimit;
        const visibleMatches = baseVisibleCount > 0 ? allMatches.slice(0, baseVisibleCount) : [...allMatches];

        pinnedAcceptedCandidates.forEach((candidate) => {
            if (!visibleMatches.some((existing) => isSameAcceptedCandidate(existing, candidate, layer))) {
                visibleMatches.push(candidate);
            }
        });

        return {
            matches: visibleMatches,
            totalAvailable: allMatches.length,
            shownCount: visibleMatches.length,
            canLoadMore: visibleMatches.length < allMatches.length,
            canShowLess: hasCustomVisibleCount && allMatches.length > Math.max(0, displayLimit),
            visibilityKey,
        };
    };

    const handleLoadMoreMatches = (columnKey, layer, path) => {
        const visibilityKey = getRowMatchVisibilityKey(columnKey, layer, path);
        const currentVisibleCount = Number(rowVisibleMatchCounts[visibilityKey] || 0);
        const displayLimit = getDisplayLimit();
        const nextVisibleCount = (currentVisibleCount > 0 ? currentVisibleCount : displayLimit) + getRowLoadMoreSize();
        setRowVisibleMatchCounts((prev) => ({
            ...prev,
            [visibilityKey]: nextVisibleCount,
        }));
    };
    const handleShowLessMatches = (columnKey, layer, path) => {
        const visibilityKey = getRowMatchVisibilityKey(columnKey, layer, path);
        setRowVisibleMatchCounts((prev) => {
            const next = { ...prev };
            delete next[visibilityKey];
            return next;
        });
    };

    const renderMatchVisibilitySummary = (shownMatchCount, totalAvailableMatches) => {
        if (totalAvailableMatches <= 0) return null;
        return (
            <Typography variant="caption" color="text.secondary">
                Showing {shownMatchCount} of {totalAvailableMatches}
            </Typography>
        );
    };

    const renderMatchVisibilityControls = (col, activeLayer, activePath, canLoadMoreMatches, canShowLessMatches) => {
        if (!canLoadMoreMatches && !canShowLessMatches) return null;

        return (
            <Stack spacing={0.25} alignItems="center" sx={{ width: '100%' }}>
                {canLoadMoreMatches && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => handleLoadMoreMatches(col.column_key, activeLayer, activePath)}
                        sx={{ minWidth: 0, px: 0.5, py: 0, lineHeight: 1.2 }}
                    >
                        Load More
                    </Button>
                )}
                {canShowLessMatches && (
                    <Button
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={() => handleShowLessMatches(col.column_key, activeLayer, activePath)}
                        sx={{ minWidth: 0, px: 0.5, py: 0, lineHeight: 1.2 }}
                    >
                        Show Less
                    </Button>
                )}
            </Stack>
        );
    };

    const getLayeredMatches = (col, selectedLayer, selectedPath = null) => {
        const layers = selectedLayer ? [selectedLayer] : ['L2', 'L3'];
        const flattened = [];
        let fallbackIndex = 0;
        let totalAvailable = 0;
        let shownCount = 0;
        let canLoadMore = false;
        let activePathResult = selectedPath;

        layers.forEach((layer) => {
            const activePath = selectedPath || getDefaultMatchPathForColumn(col, layer);
            const matchView = getVisibleMatchesForPath(col, layer, activePath);
            let layerMatches = [...matchView.matches];
            const layerDecision = getLayerDecision(col, layer);
            totalAvailable = matchView.totalAvailable;
            shownCount = matchView.shownCount;
            canLoadMore = matchView.canLoadMore;
            activePathResult = activePath;
            layerMatches.sort((left, right) => compareMatchesForDisplay(col, layer, left, right));
            const acceptedCandidates = getAcceptedCandidates(layerDecision, layer);
            const manualAcceptedCandidates = acceptedCandidates.filter((candidate) => isManualAcceptedCandidate(candidate));
            if (manualAcceptedCandidates.length > 0 && layerMatches.length > 0) {
                const replacementMatches = [...layerMatches];
                manualAcceptedCandidates.forEach((manualCandidate) => {
                    const manualPath = normalizeMatchPath(manualCandidate?.match_path || layerDecision?.match_path, '');
                    if (manualPath && manualPath !== activePath && manualPath !== 'manual') return;

                    const replacementMatch = {
                        ...manualCandidate,
                        layer,
                        matchPath: manualPath === 'manual' ? activePath : (manualPath || activePath),
                    };
                    const existingManualIndex = replacementMatches.findIndex((match) => isSameAcceptedCandidate(manualCandidate, match, layer));
                    const targetIndex = findManualOverrideTargetIndex(replacementMatches, manualCandidate, layer, activePath);
                    if (targetIndex >= 0) {
                        replacementMatches[targetIndex] = replacementMatch;
                        if (existingManualIndex >= 0 && existingManualIndex !== targetIndex) {
                            replacementMatches.splice(existingManualIndex, 1);
                        }
                        return;
                    }
                    if (existingManualIndex >= 0) {
                        replacementMatches[existingManualIndex] = replacementMatch;
                        return;
                    }
                    replacementMatches.push(replacementMatch);
                });
                layerMatches = replacementMatches;
            }
            const layerCaption = `${layer} ${activePath === 'exact' ? 'Exact Matches' : 'Suggestions'}`;

            if (layerMatches.length === 0) {
                flattened.push({
                    layer,
                    matchPath: activePath,
                    isPlaceholder: true,
                    layerCaption,
                    combinedIndex: null,
                });
                return;
            }

            layerMatches.forEach((match, index) => {
                flattened.push({
                    ...match,
                    layer,
                    matchPath: activePath,
                    layerCaption,
                    isPlaceholder: false,
                    showLayerLabel: index === 0,
                    combinedIndex: typeof match._combined_index === 'number' ? match._combined_index : fallbackIndex++,
                });
            });
        });

        if (flattened.length === 0 && Array.isArray(col.matches) && col.matches.length > 0) {
            const filtered = selectedLayer
                ? col.matches.filter(m => (m.catalyst_layer || 'Unknown') === selectedLayer)
                : [...col.matches];
            filtered.sort((left, right) => compareMatchesForDisplay(col, selectedLayer || left?.catalyst_layer || right?.catalyst_layer || 'L2', left, right));
            const fallbackMatches = filtered.map((match, index) => ({
                ...match,
                layer: match.catalyst_layer || 'Unknown',
                layerCaption: `${match.catalyst_layer || 'Matches'} Suggestions`,
                isPlaceholder: false,
                showLayerLabel: index === 0,
                combinedIndex: typeof match._combined_index === 'number' ? match._combined_index : index,
            }));
            return {
                matches: fallbackMatches,
                totalAvailable: fallbackMatches.length,
                shownCount: fallbackMatches.length,
                canLoadMore: false,
                activePath: activePathResult,
            };
        }

        return {
            matches: flattened,
            totalAvailable,
            shownCount,
            canLoadMore,
            activePath: activePathResult,
        };
    };

    const getVisibleLayerMatches = (col, layer) => {
        const activePath = rowMatchPathFilter[`${col.column_key}:${layer}`] || getDefaultMatchPathForColumn(col, layer);
        return getLayeredMatches(col, layer, activePath);
    };

    const getDefaultLayerForColumn = (col) => {
        const acceptedLayers = ['L2', 'L3']
            .map((layer) => [layer, getLayerDecision(col, layer)])
            .filter(([layer, decision]) => decision?.status === 'accepted' && getAcceptedCandidates(decision, layer).length > 0)
            .sort((left, right) => {
                const timestampDelta = getDecisionTimestamp(right[1]) - getDecisionTimestamp(left[1]);
                if (timestampDelta !== 0) return timestampDelta;
                return left[0] === 'L3' ? -1 : 1;
            });
        if (acceptedLayers.length > 0) return acceptedLayers[0][0];
        if (getLayerDecision(col, 'L2').status === 'rejected' && getLayerDecision(col, 'L3').status !== 'rejected') return 'L3';
        if (getLayerDecision(col, 'L3').status === 'rejected' && getLayerDecision(col, 'L2').status !== 'rejected') return 'L2';
        if (Array.isArray(col?.exact_matches_by_layer?.L2) && col.exact_matches_by_layer.L2.length > 0) {
            return 'L2';
        }
        if (Array.isArray(col?.exact_matches_by_layer?.L3) && col.exact_matches_by_layer.L3.length > 0) {
            return 'L3';
        }
        const matchesByLayer = col.matches_by_layer || {};
        if (Array.isArray(matchesByLayer.L2) && matchesByLayer.L2.length > 0) {
            return 'L2';
        }
        if (Array.isArray(matchesByLayer.L3) && matchesByLayer.L3.length > 0) {
            return 'L3';
        }
        return 'L2';
    };

    const getMatchStageLabel = (stage) => {
        switch (stage) {
            case 'user_preference':
                return 'User Preference';
            case 'same_theme_priority':
                return 'Same Theme Priority';
            case 'other_priority_tables':
                return 'Other Priority Tables';
            case 'same_theme_all':
                return 'Same Theme All Tables';
            case 'combined_theme_priority':
                return 'Combined Theme Priority';
            case 'combined_theme_all':
                return 'Combined Theme All Tables';
            case 'other_theme_priority':
                return 'Other Theme Priority';
            case 'other_theme_all':
                return 'Other Theme All Tables';
            case 'all_tables':
                return 'All Tables';
            case 'fallback':
                return 'Fallback';
            case 'manual':
                return 'Manual Selection';
            default:
                return stage || 'Unknown';
        }
    };

    const getDecisionColor = (status) => {
        if (status === 'accepted') return 'success';
        if (status === 'rejected') return 'error';
        return 'warning';
    };

    const renderLayerToggle = (col, activeLayer) => (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, justifyContent: 'flex-start' }}>
            {['L2', 'L3'].map(layer => (
                <Chip
                    key={layer}
                    label={layer}
                    size="small"
                    variant={activeLayer === layer ? 'filled' : 'outlined'}
                    color={activeLayer === layer ? 'primary' : 'default'}
                    onClick={() => setRowLayerFilter(prev => ({ ...prev, [col.column_key]: layer }))}
                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer' }}
                />
            ))}
        </Box>
    );

    const renderMatchPathToggle = (col, activeLayer) => {
        const exactMatches = getAllMatchesForPath(col, activeLayer, 'exact');
        const semanticMatches = getAllMatchesForPath(col, activeLayer, 'semantic');
        if (!exactMatches.length && !semanticMatches.length) {
            return null;
        }
        const filterKey = `${col.column_key}:${activeLayer}`;
        const activePath = rowMatchPathFilter[filterKey] || getDefaultMatchPathForColumn(col, activeLayer);
        const options = [
            { value: 'exact', label: 'Exact Match', available: exactMatches.length > 0 },
            { value: 'semantic', label: 'Semantic Match', available: semanticMatches.length > 0 },
        ].filter(option => option.available);

        return (
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, justifyContent: 'flex-start' }}>
                {options.map(option => (
                    <Chip
                        key={option.value}
                        label={option.label}
                        size="small"
                        variant={activePath === option.value ? 'filled' : 'outlined'}
                        color={activePath === option.value ? 'primary' : 'default'}
                        onClick={() => setRowMatchPathFilter(prev => ({ ...prev, [filterKey]: option.value }))}
                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer' }}
                    />
                ))}
            </Box>
        );
    };

    const renderMatchInfoTooltip = (col, match) => {
        const matchLayer = match.layer || match.catalyst_layer || 'Unknown';
        const searchScope = match.matchPath === 'exact' ? 'Exact column / attribute match' : 'Layer-wide semantic search';

        return (
            <Box sx={{ py: 0.5 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                    Match Details
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                    Layer: {matchLayer}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                    Stage: {getMatchStageLabel(match.match_stage)}
                </Typography>
                {typeof match.llm_confidence === 'number' && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                        LLM Confidence: {`${(getDisplayMatchConfidence(match) * 100).toFixed(0)}%`}
                    </Typography>
                )}
                {typeof match.vector_score === 'number' && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                        Semantic Score: {`${((match.vector_score || 0) * 100).toFixed(0)}%`}
                    </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block' }}>
                    Search Scope: {searchScope}
                </Typography>
            </Box>
        );
    };

    const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

    const formatConfidence = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
        return `${(Number(value) * 100).toFixed(0)}%`;
    };

    const renderStackedBar = (segments, height = 16) => (
        <Box sx={{ width: '100%', height, display: 'flex', borderRadius: 999, overflow: 'hidden', bgcolor: 'grey.200' }}>
            {segments.map((segment) => {
                const meta = ANALYTICS_SEGMENT_META[segment.key] || { color: 'grey.500', label: segment.label };
                if (!segment.percent) return null;
                return (
                    <Tooltip
                        key={segment.key}
                        title={`${segment.label}: ${segment.count} (${formatPercent(segment.percent)})`}
                        arrow
                    >
                        <Box
                            sx={{
                                width: `${segment.percent}%`,
                                bgcolor: meta.color,
                                minWidth: segment.percent > 0 ? 6 : 0,
                            }}
                        />
                    </Tooltip>
                );
            })}
        </Box>
    );

    const renderAnalyticsLegend = (segments) => (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {segments.map((segment) => {
                const meta = ANALYTICS_SEGMENT_META[segment.key] || { color: 'grey.500' };
                return (
                    <Box key={segment.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: meta.color }} />
                        <Typography variant="caption" color="text.secondary">
                            {segment.label}: {segment.count} ({formatPercent(segment.percent)})
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );

    const renderAnalyticsSection = () => {
        if (analyticsLoading) {
            return (
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width={260} height={36} />
                        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                    </CardContent>
                </Card>
            );
        }

        if (!analyticsData) {
            return (
                <Alert severity="info">
                    No analytics available yet. Process the selected file first.
                </Alert>
            );
        }

        const thresholdPercent = Math.round((analyticsData?.thresholds?.semantic_high_confidence || 0.8) * 100);
        const kpis = analyticsData.kpis || {};
        const coverageSegments = analyticsData.overall_coverage || [];
        const layerSummary = analyticsData.layer_summary || [];
        const confidenceDistribution = analyticsData.semantic_confidence_distribution || [];
        const stageSummary = analyticsData.stage_summary || [];
        const noMatchRows = analyticsData.no_match_rows || [];
        const lowConfidenceRows = analyticsData.low_confidence_rows || [];

        return (
            <Stack spacing={3}>
                <Grid container spacing={2}>
                    {[
                        { label: 'Total Source Fields', value: kpis.total_fields || 0, helper: 'Legacy fields in the selected file' },
                        { label: 'Mapped Fields', value: `${kpis.mapped_fields || 0} (${formatPercent(kpis.coverage_percent)})`, helper: 'Exact, semantic high-confidence, and manual mappings' },
                        { label: 'Exact Match', value: `${kpis.exact_fields || 0} (${formatPercent(kpis.exact_percent)})`, helper: 'Exact physical or attribute-name matches' },
                        { label: `Semantic >=${thresholdPercent}%`, value: `${kpis.semantic_high_confidence_fields || 0} (${formatPercent(kpis.semantic_high_confidence_percent)})`, helper: 'LLM-approved semantic matches above the confidence gate' },
                        { label: 'No Match', value: `${kpis.no_match_fields || 0} (${formatPercent(kpis.no_match_percent)})`, helper: 'No exact match and no semantic match above the confidence gate' },
                    ].map((item) => (
                        <Grid item xs={12} sm={6} md={4} lg={2} key={item.label}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{item.value}</Typography>
                                    <Typography variant="caption" color="text.secondary">{item.helper}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} gutterBottom>Overall Coverage</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Exact matches take precedence, then semantic matches above the confidence gate, then unmatched coverage.
                                </Typography>
                                {renderStackedBar(coverageSegments, 22)}
                                <Box sx={{ mt: 2 }}>
                                    {renderAnalyticsLegend(coverageSegments)}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} gutterBottom>L2 vs L3 Coverage</Typography>
                                <Stack spacing={2} sx={{ mt: 1 }}>
                                    {layerSummary.map((layer) => {
                                        const segments = [
                                            { key: 'exact', label: 'Exact Match', count: layer.exact || 0, percent: layer.total_fields ? ((layer.exact || 0) / layer.total_fields) * 100 : 0 },
                                            { key: 'semantic_high_confidence', label: `Semantic >=${thresholdPercent}%`, count: layer.semantic_high_confidence || 0, percent: layer.total_fields ? ((layer.semantic_high_confidence || 0) / layer.total_fields) * 100 : 0 },
                                            { key: 'manual', label: 'Manual', count: layer.manual || 0, percent: layer.total_fields ? ((layer.manual || 0) / layer.total_fields) * 100 : 0 },
                                            { key: 'semantic_low_confidence', label: `Semantic <${thresholdPercent}%`, count: layer.semantic_low_confidence || 0, percent: layer.total_fields ? ((layer.semantic_low_confidence || 0) / layer.total_fields) * 100 : 0 },
                                            { key: 'no_match', label: 'No Match', count: layer.no_match || 0, percent: layer.total_fields ? ((layer.no_match || 0) / layer.total_fields) * 100 : 0 },
                                        ];
                                        return (
                                            <Box key={layer.layer}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                                    <Typography variant="subtitle2" fontWeight={700}>{layer.layer}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Coverage {formatPercent(layer.coverage_percent)}
                                                    </Typography>
                                                </Box>
                                                {renderStackedBar(segments, 18)}
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} gutterBottom>Best Semantic Confidence Distribution</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Distribution of the strongest semantic candidate per source field across L2 and L3.
                                </Typography>
                                <Stack spacing={1.25}>
                                    {confidenceDistribution.map((band) => {
                                        const percent = kpis.total_fields ? ((band.count || 0) / kpis.total_fields) * 100 : 0;
                                        return (
                                            <Box key={band.key}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" fontWeight={600}>{band.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {band.count} fields ({formatPercent(percent)})
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ width: '100%', height: 12, borderRadius: 999, bgcolor: 'grey.200', overflow: 'hidden' }}>
                                                    <Box sx={{ width: `${percent}%`, height: '100%', bgcolor: '#5c6bc0' }} />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} gutterBottom>Stage Distribution</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Stage counts for the strongest candidate retained per layer. Useful for understanding where the matches are coming from.
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>L2</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>L3</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Avg LLM</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stageSummary.map((row) => (
                                            <TableRow key={row.stage}>
                                                <TableCell>{row.label}</TableCell>
                                                <TableCell>{row.L2}</TableCell>
                                                <TableCell>{row.L3}</TableCell>
                                                <TableCell>{formatConfidence(row.avg_llm_confidence)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} gutterBottom>Layer Summary</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Layer</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Exact</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>{`Semantic >=${thresholdPercent}%`}</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>{`Semantic <${thresholdPercent}%`}</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Fallback Seen</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Manual</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>No Match</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Coverage</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {layerSummary.map((row) => (
                                            <TableRow key={row.layer}>
                                                <TableCell sx={{ fontWeight: 700 }}>{row.layer}</TableCell>
                                                <TableCell>{row.exact}</TableCell>
                                                <TableCell>{row.semantic_high_confidence}</TableCell>
                                                <TableCell>{row.semantic_low_confidence}</TableCell>
                                                <TableCell>{row.fallback}</TableCell>
                                                <TableCell>{row.manual}</TableCell>
                                                <TableCell>{row.no_match}</TableCell>
                                                <TableCell>{formatPercent(row.coverage_percent)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} gutterBottom>No Match</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Fields with no exact match and no semantic candidate above {thresholdPercent}% confidence.
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Legacy Field</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Best L2</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Best L3</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {noMatchRows.slice(0, 15).map((row) => (
                                            <TableRow key={row.column_key}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>{row.legacy_table}.{row.legacy_column}</Typography>
                                                    {row.legacy_attribute && (
                                                        <Typography variant="caption" color="text.secondary">{row.legacy_attribute}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>{formatConfidence(row.l2_best_semantic_confidence)}</TableCell>
                                                <TableCell>{formatConfidence(row.l3_best_semantic_confidence)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {noMatchRows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="caption" color="text.secondary">No rows in this category.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} gutterBottom>Low Confidence Semantic</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Fields that have semantic candidates, but none cleared the {thresholdPercent}% confidence gate.
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Legacy Field</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Best Layer</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Best Confidence</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {lowConfidenceRows.slice(0, 15).map((row) => (
                                            <TableRow key={row.column_key}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>{row.legacy_table}.{row.legacy_column}</Typography>
                                                    {row.legacy_attribute && (
                                                        <Typography variant="caption" color="text.secondary">{row.legacy_attribute}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>{row.best_semantic_layer || '-'}</TableCell>
                                                <TableCell>{formatConfidence(row.best_semantic_confidence)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {lowConfidenceRows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="caption" color="text.secondary">No rows in this category.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Stack>
        );
    };

    const handleDiagnosticTargetTableChange = async (tableOption) => {
        setDiagnosticTargetTable(tableOption);
        setDiagnosticTargetColumn(null);
        setDiagnosticTargetColumns([]);
        setDiagnosticTargetColumnInput('');

        if (!tableOption?.table_name) {
            return;
        }

        const cacheKey = `${diagnosticTargetLayer}:${normalizeIdentifier(tableOption.table_name)}`;
        if (diagnosticTargetColumnsCache[cacheKey]) {
            setDiagnosticTargetColumns(diagnosticTargetColumnsCache[cacheKey]);
            return;
        }

        const tableKey = normalizeIdentifier(tableOption.table_name);
        const catalogColumns = Array.isArray(diagnosticTargetColumnsByLayer?.[diagnosticTargetLayer]?.[tableKey])
            ? diagnosticTargetColumnsByLayer[diagnosticTargetLayer][tableKey]
            : [];
        const uniqueColumns = [];
        const seenColumns = new Set();

        catalogColumns.forEach((column) => {
            const columnKey = `${normalizeIdentifier(column?.table_name)}|${normalizeIdentifier(column?.column_name)}`;
            if (!column?.column_name || seenColumns.has(columnKey)) return;
            seenColumns.add(columnKey);
            uniqueColumns.push(column);
        });

        uniqueColumns.sort((left, right) => String(left?.column_name || '').localeCompare(String(right?.column_name || '')));
        setDiagnosticTargetColumns(uniqueColumns);
        setDiagnosticTargetColumnsCache((prev) => ({
            ...prev,
            [cacheKey]: uniqueColumns,
        }));
    };

    const getDiagnosticMatchesForPath = (fieldData, layer, path) => {
        const layerOutput = fieldData?.mapping_outputs?.[layer] || {};
        const matches = path === 'exact' ? layerOutput.exact_matches : layerOutput.semantic_matches;
        return Array.isArray(matches) ? [...matches].sort((left, right) => getMatchSortScore(right) - getMatchSortScore(left)) : [];
    };

    const renderDiagnosticFieldCard = (title, details, emptyMessage, accentColor) => (
        <Card sx={{ height: '100%', borderTop: `4px solid ${accentColor}` }}>
            <CardContent>
                <Typography variant="overline" color="text.secondary">{title}</Typography>
                {details ? (
                    <Stack spacing={1.25}>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {details.table_name}.{details.column_name}
                            </Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                {details.layer && <Chip label={details.layer} size="small" color="primary" variant="outlined" />}
                                <Chip label={`Theme: ${details.theme || '-'}`} size="small" color="secondary" variant="outlined" />
                                {details.data_type && <Chip label={details.data_type} size="small" variant="outlined" />}
                            </Stack>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Attribute Name</Typography>
                            <Typography variant="body2">{details.attribute_name || '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Definition</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{details.definition || '-'}</Typography>
                        </Box>
                        {details.show_semantic_score && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Semantic Score vs Source</Typography>
                                {details.semantic_score_loading ? (
                                    <Skeleton variant="rounded" width={92} height={28} sx={{ mt: 0.5 }} />
                                ) : typeof details.semantic_score === 'number' ? (
                                    <Stack direction="row" sx={{ mt: 0.75 }}>
                                        <Chip
                                            label={formatConfidence(details.semantic_score)}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                        />
                                    </Stack>
                                ) : (
                                    <Typography variant="body2">-</Typography>
                                )}
                            </Box>
                        )}
                        {details.show_target_trace && (
                            <>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Matching Stage</Typography>
                                    {details.target_trace_loading ? (
                                        <Skeleton variant="text" width={180} sx={{ mt: 0.25 }} />
                                    ) : (
                                        <Typography variant="body2">{details.target_trace_stage || '-'}</Typography>
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Bucket Position</Typography>
                                    {details.target_trace_loading ? (
                                        <Skeleton variant="text" width={120} sx={{ mt: 0.25 }} />
                                    ) : (
                                        <Typography variant="body2">{details.target_trace_position || '-'}</Typography>
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">LLM Validation</Typography>
                                    {details.target_trace_loading ? (
                                        <Skeleton variant="text" width={120} sx={{ mt: 0.25 }} />
                                    ) : (
                                        <Typography variant="body2">{details.target_trace_llm || '-'}</Typography>
                                    )}
                                </Box>
                                {details.target_trace_llm_rejection_reason && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">LLM Rejection Reason</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {details.target_trace_llm_rejection_reason}
                                        </Typography>
                                    </Box>
                                )}
                                {details.target_trace_reason && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Why Not In Bucket</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {details.target_trace_reason}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Table</Typography>
                            <Typography variant="body2">{details.table_name}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Entity / Table Description</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {[details.entity_name, details.table_definition].filter(Boolean).join(' | ') || '-'}
                            </Typography>
                        </Box>
                    </Stack>
                ) : (
                    <Alert severity="info" sx={{ mt: 1.5 }}>{emptyMessage}</Alert>
                )}
            </CardContent>
        </Card>
    );

    const renderDiagnosticMatchBucket = (fieldData, layer, path) => {
        const matches = getDiagnosticMatchesForPath(fieldData, layer, path);
        const layerDecision = fieldData?.mapping_outputs?.[layer]?.decision || { status: 'pending' };
        const bucketTitle = `${layer} ${path === 'exact' ? 'Exact Matches' : 'Semantic Matches'}`;

        return (
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" fontWeight={700}>{bucketTitle}</Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            <Chip label={`${matches.length} result${matches.length === 1 ? '' : 's'}`} size="small" variant="outlined" />
                            <Chip label={`Decision: ${layerDecision.status}`} size="small" color={getDecisionColor(layerDecision.status)} variant="outlined" />
                        </Stack>
                    </Box>

                    {matches.length === 0 ? (
                        <Alert severity="info">
                            No {path === 'exact' ? 'exact' : 'semantic'} results for {layer}.
                        </Alert>
                    ) : (
                        <Stack spacing={1.25}>
                            {matches.map((match, index) => {
                                const targetTableMeta = diagnosticTargetTableMetaByLayer[layer]?.[normalizeIdentifier(match?.catalyst_table)] || {};
                                const selectedMatchIndex = match && typeof match._combined_index === 'number' ? match._combined_index : null;
                                const resolvedStatus = getResolvedMatchDecisionStatus(layerDecision, {
                                    ...match,
                                    combinedIndex: selectedMatchIndex,
                                }, layer);
                                const isSelected = resolvedStatus === 'accepted';
                                const isRejected = resolvedStatus === 'rejected';

                                return (
                                    <Box
                                        key={`${layer}-${path}-${match?.catalyst_table || 'unknown'}-${match?.catalyst_column || 'unknown'}-${index}`}
                                        sx={{
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: isSelected ? 'success.main' : isRejected ? 'error.main' : 'divider',
                                            borderRadius: 2,
                                            bgcolor: isSelected ? 'rgba(46, 125, 50, 0.06)' : isRejected ? 'rgba(211, 47, 47, 0.06)' : 'background.default',
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={700}>
                                            {match?.catalyst_table || '-'}.
                                            <Box component="span" sx={{ color: 'primary.main' }}>{match?.catalyst_column || '-'}</Box>
                                        </Typography>
                                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1, mb: 1 }}>
                                            <Chip label={layer} size="small" color="primary" variant="outlined" />
                                            <Chip label={getMatchStageLabel(match?.match_stage)} size="small" variant="outlined" />
                                            <Chip label={`Theme: ${targetTableMeta?.theme || '-'}`} size="small" color="secondary" variant="outlined" />
                                            {typeof match?.llm_confidence === 'number' && (
                                                <Chip label={`LLM ${formatConfidence(match.llm_confidence)}`} size="small" variant="outlined" />
                                            )}
                                            {typeof match?.vector_score === 'number' && (
                                                <Chip label={`Semantic ${formatConfidence(match.vector_score)}`} size="small" variant="outlined" />
                                            )}
                                            {isSelected && <Chip label="Accepted Mapping" size="small" color="success" />}
                                            {isRejected && <Chip label="Rejected Mapping" size="small" color="error" />}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">Attribute Name</Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>{match?.catalyst_attribute || '-'}</Typography>
                                        <Typography variant="caption" color="text.secondary">Definition</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{match?.catalyst_definition || '-'}</Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderDiagnosticSelectionCard = (title, description, actionSlot = null) => (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 420px' }}>
                        <Typography variant="h6" fontWeight={700} gutterBottom>{title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                            {description}
                        </Typography>
                    </Box>
                    {actionSlot ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {actionSlot}
                        </Box>
                    ) : null}
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Source Field</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    openOnFocus
                                    options={diagnosticSourceTables}
                                    value={diagnosticSourceTable}
                                    inputValue={diagnosticSourceTableInput}
                                    onChange={(event, newValue) => {
                                        setDiagnosticSourceTable(newValue || null);
                                        setDiagnosticSourceColumn(null);
                                        setDiagnosticSourceTableInput(newValue?.table_name || '');
                                        setDiagnosticSourceColumnInput('');
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        setDiagnosticSourceTableInput(newInputValue);
                                        if (reason === 'clear') {
                                            setDiagnosticSourceTable(null);
                                            setDiagnosticSourceColumn(null);
                                            setDiagnosticSourceColumnInput('');
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => normalizeIdentifier(option?.table_name) === normalizeIdentifier(value?.table_name)}
                                    getOptionLabel={(option) => option?.table_name || ''}
                                    renderInput={(params) => <TextField {...params} label="Source Table" placeholder="Search table..." />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    openOnFocus
                                    options={Array.isArray(diagnosticSourceTable?.columns) ? diagnosticSourceTable.columns : []}
                                    value={diagnosticSourceColumn}
                                    inputValue={diagnosticSourceColumnInput}
                                    onChange={(event, newValue) => {
                                        setDiagnosticSourceColumn(newValue || null);
                                        setDiagnosticSourceColumnInput(
                                            newValue ? `${newValue?.column_name || ''}${newValue?.attribute_name ? ` - ${newValue.attribute_name}` : ''}` : ''
                                        );
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        setDiagnosticSourceColumnInput(newInputValue);
                                        if (reason === 'clear') {
                                            setDiagnosticSourceColumn(null);
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.column_key === value?.column_key}
                                    getOptionLabel={(option) => `${option?.column_name || ''}${option?.attribute_name ? ` - ${option.attribute_name}` : ''}`}
                                    renderInput={(params) => <TextField {...params} label="Source Column" placeholder="Search column..." />}
                                    disabled={!diagnosticSourceTable}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Ground Truth Target</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Layer</InputLabel>
                                    <Select
                                        value={diagnosticTargetLayer}
                                        onChange={(event) => {
                                            const newLayer = event.target.value;
                                            setDiagnosticTargetLayer(newLayer);
                                            setDiagnosticTargetTable(null);
                                            setDiagnosticTargetColumn(null);
                                            setDiagnosticTargetColumns([]);
                                            setDiagnosticTargetTableInput('');
                                            setDiagnosticTargetColumnInput('');
                                        }}
                                        label="Layer"
                                    >
                                        {DIAGNOSTIC_LAYERS.map((layer) => (
                                            <MenuItem key={layer} value={layer}>{layer}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4.5}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    openOnFocus
                                    options={diagnosticTargetTablesByLayer[diagnosticTargetLayer] || []}
                                    loading={diagnosticTargetCatalogLoading}
                                    value={diagnosticTargetTable}
                                    inputValue={diagnosticTargetTableInput}
                                    onChange={(event, newValue) => {
                                        setDiagnosticTargetTableInput(newValue?.table_name || '');
                                        handleDiagnosticTargetTableChange(newValue);
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        setDiagnosticTargetTableInput(newInputValue);
                                        if (reason === 'clear') {
                                            setDiagnosticTargetTable(null);
                                            setDiagnosticTargetColumn(null);
                                            setDiagnosticTargetColumns([]);
                                            setDiagnosticTargetColumnInput('');
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => normalizeIdentifier(option?.table_name) === normalizeIdentifier(value?.table_name)}
                                    getOptionLabel={(option) => option?.table_name || ''}
                                    renderInput={(params) => <TextField {...params} label="Target Table" placeholder="Search table..." />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4.5}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    openOnFocus
                                    options={diagnosticTargetColumns}
                                    value={diagnosticTargetColumn}
                                    inputValue={diagnosticTargetColumnInput}
                                    onChange={(event, newValue) => {
                                        setDiagnosticTargetColumn(newValue || null);
                                        setDiagnosticTargetColumnInput(
                                            newValue ? `${newValue?.column_name || ''}${newValue?.attribute_name ? ` - ${newValue.attribute_name}` : ''}` : ''
                                        );
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        setDiagnosticTargetColumnInput(newInputValue);
                                        if (reason === 'clear') {
                                            setDiagnosticTargetColumn(null);
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => normalizeIdentifier(option?.column_name) === normalizeIdentifier(value?.column_name)}
                                    getOptionLabel={(option) => `${option?.column_name || ''}${option?.attribute_name ? ` - ${option.attribute_name}` : ''}`}
                                    renderInput={(params) => <TextField {...params} label="Target Column" placeholder="Search column..." />}
                                    disabled={!diagnosticTargetTable}
                                    loading={diagnosticTargetLoadingColumns}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderDiagnosticsSection = () => {
        if (!mappingData) {
            return (
                <Alert severity="info">
                    No mapping results available yet. Process the selected file first.
                </Alert>
            );
        }

        if (diagnosticCatalogLoading && diagnosticReadyFileId !== selectedFile?.id) {
            return (
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width={260} height={36} />
                        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
                    </CardContent>
                </Card>
            );
        }

        const sourceDetails = diagnosticFieldData?.source ? {
            table_name: diagnosticFieldData.source.table_name,
            column_name: diagnosticFieldData.source.column_name,
            attribute_name: diagnosticFieldData.source.attribute_name || '-',
            definition: diagnosticFieldData.source.definition || '-',
            data_type: diagnosticFieldData.source.data_type || '-',
            theme: diagnosticFieldData.source.theme || '-',
            entity_name: diagnosticFieldData.source.entity_name || '',
            table_definition: diagnosticFieldData.source.table_definition || '',
        } : null;

        const selectedTargetTableMeta = diagnosticTargetTable
            ? diagnosticTargetTableMetaByLayer[diagnosticTargetLayer]?.[normalizeIdentifier(diagnosticTargetTable.table_name)] || diagnosticTargetTable
            : null;
        const targetTraceStage = diagnosticTargetTrace?.stage_label || diagnosticTargetTrace?.stage_name || null;
        const targetTracePosition = Number.isInteger(diagnosticTargetTrace?.bucket_position) && Number.isInteger(diagnosticTargetTrace?.bucket_size)
            ? `${diagnosticTargetTrace.bucket_position} of ${diagnosticTargetTrace.bucket_size}`
            : (diagnosticTargetTrace?.retrieved_in_bucket === false ? 'Not retrieved in bucket' : null);
        const targetTraceLlm = diagnosticTargetTrace
            ? (
                diagnosticTargetTrace.went_to_llm_validation
                    ? (diagnosticTargetTrace.llm_approved ? 'Yes, approved' : (diagnosticTargetTrace.llm_ranked ? 'Yes, ranked' : 'Yes'))
                    : (
                        diagnosticTargetTrace.llm_validation_status === 'not_needed_exact_match'
                            ? 'No, exact match'
                            : 'No'
                    )
            )
            : null;
        const targetTraceReason = diagnosticTargetTrace?.retrieved_in_bucket === false
            ? (diagnosticTargetTrace?.not_in_bucket_reason || null)
            : null;
        const targetTraceLlmRejectionReason = diagnosticTargetTrace?.llm_validation_status === 'rejected'
            ? (diagnosticTargetTrace?.llm_rejection_reason || null)
            : null;
        const targetDetails = diagnosticTargetTable && diagnosticTargetColumn ? {
            layer: diagnosticTargetLayer,
            table_name: diagnosticTargetTable.table_name,
            column_name: diagnosticTargetColumn.column_name,
            attribute_name: diagnosticTargetColumn.attribute_name || '-',
            definition: diagnosticTargetColumn.definition || '-',
            data_type: diagnosticTargetColumn.data_type || '-',
            theme: diagnosticTargetColumn.theme || selectedTargetTableMeta?.theme || '-',
            entity_name: selectedTargetTableMeta?.entity_name || '',
            table_definition: selectedTargetTableMeta?.definition || '',
            show_semantic_score: true,
            semantic_score: diagnosticSemanticScore,
            semantic_score_loading: diagnosticSemanticScoreLoading,
            show_target_trace: true,
            target_trace_loading: diagnosticTargetTraceLoading,
            target_trace_stage: targetTraceStage,
            target_trace_position: targetTracePosition,
            target_trace_llm: targetTraceLlm,
            target_trace_llm_rejection_reason: targetTraceLlmRejectionReason,
            target_trace_reason: targetTraceReason,
        } : null;

        return (
            <Stack spacing={3}>
                {renderDiagnosticSelectionCard(
                    'Diagnostics',
                    'Compare the selected source field with the tool-provided L2/L3 mappings and a manually selected ground-truth target field. This view only shows metadata from mapping output and uploaded dictionaries.',
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} lg={3}>
                        {renderDiagnosticFieldCard(
                            'Source Field Details',
                            sourceDetails,
                            'Select a source table and column to inspect the source field metadata.',
                            '#1565c0',
                        )}
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary">Tool Mapping Output</Typography>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                                    {diagnosticFieldData?.source
                                        ? `${diagnosticFieldData.source.table_name}.${diagnosticFieldData.source.column_name}`
                                        : 'Select a source field'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Use the panels below to compare table theme, attribute name, and definition across the tool's suggested mappings and the selected ground truth target.
                                </Typography>

                                {diagnosticFieldLoading ? (
                                    <Stack spacing={1.5}>
                                        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
                                        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
                                    </Stack>
                                ) : diagnosticFieldData ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            {renderDiagnosticMatchBucket(diagnosticFieldData, 'L2', 'exact')}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {renderDiagnosticMatchBucket(diagnosticFieldData, 'L2', 'semantic')}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {renderDiagnosticMatchBucket(diagnosticFieldData, 'L3', 'exact')}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            {renderDiagnosticMatchBucket(diagnosticFieldData, 'L3', 'semantic')}
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Alert severity="info">
                                        Select a source field to view its L2 and L3 exact and semantic mapping results.
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        {renderDiagnosticFieldCard(
                            'Ground Truth Target Details',
                            targetDetails,
                            'Select a target layer, table, and column to compare against the mapping output.',
                            '#6a1b9a',
                        )}
                    </Grid>
                </Grid>
            </Stack>
        );
    };

    const renderPipelineSection = () => {
        if (!mappingData) {
            return <Alert severity="info">No mapping results available yet. Process the selected file first.</Alert>;
        }

        const hasPairSelection = Boolean(
            diagnosticSourceColumn?.column_key
            && diagnosticTargetLayer
            && diagnosticTargetTable?.table_name
            && diagnosticTargetColumn?.column_name
        );
        const playbackSteps = Array.isArray(diagnosticLiveTrace?.playback_steps) ? diagnosticLiveTrace.playback_steps : [];
        const activeStep = playbackSteps[diagnosticLiveReplayIndex] || null;
        const activeStage = activeStep?.kind === 'validation_stage' ? activeStep.stage_name : null;
        const isStepVisible = (key) => diagnosticLiveReplayIndex >= playbackSteps.findIndex((step) => step.key === key);
        const isStageVisible = (stageName) => diagnosticLiveReplayIndex >= playbackSteps.findIndex((step) => step.kind === 'validation_stage' && step.stage_name === stageName);
        const progressPercent = typeof diagnosticLiveProgress?.percent === 'number'
            ? diagnosticLiveProgress.percent
            : (playbackSteps.length > 0 ? Math.min(100, ((diagnosticLiveReplayIndex + 1) / playbackSteps.length) * 100) : 0);
        const runLivePipeline = async () => {
            if (
                !selectedFile
                || !diagnosticSourceColumn?.column_key
                || !diagnosticTargetLayer
                || !diagnosticTargetTable?.table_name
                || !diagnosticTargetColumn?.column_name
            ) {
                setError('Select a source field and target field before running the pipeline.');
                return;
            }
            const selectedSourceTableEntry = diagnosticSourceTables.find(
                (table) => normalizeIdentifier(table?.table_name) === normalizeIdentifier(diagnosticSourceTable?.table_name),
            );
            const sourceStillValid = (selectedSourceTableEntry?.columns || []).some(
                (column) => column?.column_key === diagnosticSourceColumn?.column_key,
            );
            const targetTableStillValid = (diagnosticTargetTablesByLayer[diagnosticTargetLayer] || []).some(
                (table) => normalizeIdentifier(table?.table_name) === normalizeIdentifier(diagnosticTargetTable?.table_name),
            );
            const targetColumnStillValid = (diagnosticTargetColumns || []).some(
                (column) => normalizeIdentifier(column?.column_name) === normalizeIdentifier(diagnosticTargetColumn?.column_name),
            );
            if (!sourceStillValid || !targetTableStillValid || !targetColumnStillValid) {
                setError('The selected source/target pair is stale for the current file. Re-select the fields and submit again.');
                return;
            }
            try {
                setError(null);
                setDiagnosticLiveTrace(null);
                setDiagnosticLiveTraceLoading(true);
                setDiagnosticLiveRunId(null);
                setDiagnosticLiveReplayIndex(0);
                setDiagnosticLiveExpandedPanel('exact_lookup');
                setDiagnosticLiveStatusMessage('Queueing live pipeline replay...');
                setDiagnosticLiveProgress({ current: 0, total: null, percent: 0 });
                const response = await startMappingDiagnosticLiveTrace(
                    projectId,
                    selectedFile.sourceSystem,
                    selectedFile.id,
                    diagnosticSourceColumn.column_key,
                    diagnosticTargetLayer,
                    diagnosticTargetTable.table_name,
                    diagnosticTargetColumn.column_name,
                );
                setDiagnosticLiveRunId(response?.run_id || null);
                setDiagnosticLivePipelineRunning(response?.status === 'running');
                setDiagnosticLiveStatusMessage(response?.message || 'Live pipeline replay started.');
            } catch (err) {
                setDiagnosticLiveRunId(null);
                setDiagnosticLivePipelineRunning(false);
                setDiagnosticLiveTraceLoading(false);
                setError(err?.response?.data?.detail || err?.message || 'Failed to start live pipeline replay.');
            }
        };
        const targetDetails = diagnosticLiveTrace?.target ? {
            layer: diagnosticLiveTrace.target.layer || diagnosticTargetLayer,
            table_name: diagnosticLiveTrace.target.table_name,
            column_name: diagnosticLiveTrace.target.column_name,
            attribute_name: diagnosticLiveTrace.target.attribute_name || '-',
            definition: diagnosticLiveTrace.target.definition || '-',
            data_type: diagnosticLiveTrace.target.data_type || '-',
            theme: diagnosticLiveTrace.target.theme || '-',
            entity_name: diagnosticLiveTrace.target.entity_name || '',
            table_definition: diagnosticLiveTrace.target.table_definition || '',
            show_semantic_score: true,
            semantic_score: diagnosticLiveTrace?.semantic_search?.source_target_semantic_score ?? null,
            semantic_score_loading: diagnosticLiveTraceLoading,
        } : null;
        const sourceDetails = diagnosticLiveTrace?.source ? {
            table_name: diagnosticLiveTrace.source.table_name,
            column_name: diagnosticLiveTrace.source.column_name,
            attribute_name: diagnosticLiveTrace.source.attribute_name || '-',
            definition: diagnosticLiveTrace.source.definition || '-',
            data_type: diagnosticLiveTrace.source.data_type || '-',
            theme: diagnosticLiveTrace.source.theme || '-',
            entity_name: '',
            table_definition: '',
        } : null;
        const finalExact = diagnosticLiveTrace?.final_outcome?.exact || {};
        const finalSemantic = diagnosticLiveTrace?.final_outcome?.semantic || {};
        const finalExactDisplayed = finalExact.displayed_matches || [];
        const finalExactOverflow = finalExact.overflow_matches || [];
        const finalSemanticDisplayed = finalSemantic.displayed_matches || [];
        const finalSemanticOverflow = finalSemantic.overflow_matches || [];
        const replaySelectedLayerOutput = diagnosticLiveTrace?.selected_layer_output || null;
        const replaySelectedLayer = replaySelectedLayerOutput?.selected_layer || diagnosticLiveTrace?.target_layer || diagnosticTargetLayer || 'L2';
        const expectedTargetTrace = diagnosticLiveTrace?.expected_target_trace || null;
        const exactTargetLabel = finalExact?.target_in_displayed
            ? 'Target in Exact Top N'
            : (finalExact?.target_in_all ? 'Target in Exact Overflow' : 'Target not in Exact');
        const semanticTargetLabel = finalSemantic?.target_in_displayed
            ? 'Target in Semantic Top N'
            : (finalSemantic?.target_in_all ? 'Target in Semantic Overflow' : 'Target not in Semantic');
        const renderTraceCandidate = (candidate, options = {}) => {
            const finalDisplayState = options.finalDisplayState
                || (candidate?.in_final_display ? 'displayed' : (candidate?.skipped_after_limit ? 'skipped' : null));
            const showReasoning = options.showReasoning === true;
            const showDefinition = options.showDefinition === true;
            const borderColor = candidate?.is_target
                ? 'success.main'
                : (finalDisplayState === 'displayed' ? 'primary.main' : (finalDisplayState === 'skipped' ? 'warning.main' : 'divider'));
            const backgroundColor = candidate?.is_target
                ? 'rgba(46, 125, 50, 0.08)'
                : (finalDisplayState === 'displayed' ? 'rgba(25, 118, 210, 0.06)' : (finalDisplayState === 'skipped' ? 'rgba(237, 108, 2, 0.08)' : 'background.paper'));
            return (
            <Box
                key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-${candidate?.position || 0}`}
                sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor,
                    bgcolor: backgroundColor,
                    animation: candidate?.is_target ? 'pipelinePulse 1.6s ease-in-out infinite' : 'none',
                    '@keyframes pipelinePulse': {
                        '0%': { boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.00)' },
                        '50%': { boxShadow: '0 0 0 8px rgba(46, 125, 50, 0.10)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.00)' },
                    },
                }}
            >
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                    {Number.isInteger(candidate?.position) && <Chip label={`#${candidate.position}`} size="small" variant="outlined" />}
                    {candidate?.is_target && <Chip label="Target" size="small" color="success" />}
                    {finalDisplayState === 'displayed' && <Chip label="Displayed" size="small" color="primary" variant="outlined" />}
                    {finalDisplayState === 'skipped' && <Chip label="Not Displayed" size="small" color="warning" variant="outlined" />}
                    {candidate?.outside_bucket_limit && <Chip label="Outside kept bucket" size="small" color="warning" variant="outlined" />}
                    {candidate?.outside_preview && <Chip label="Outside preview" size="small" color="warning" variant="outlined" />}
                    {typeof candidate?.llm_confidence === 'number' && candidate?.llm_confidence > 0 && (
                        <Chip label={`LLM ${formatConfidence(candidate.llm_confidence)}`} size="small" variant="outlined" />
                    )}
                    {typeof candidate?.vector_score === 'number' && <Chip label={`Semantic ${formatConfidence(candidate.vector_score)}`} size="small" variant="outlined" />}
                </Stack>
                <Typography variant="body2" fontWeight={700}>
                    {candidate?.catalyst_table || '-'}.
                    <Box component="span" sx={{ color: candidate?.is_target ? 'success.main' : 'primary.main' }}>
                        {candidate?.catalyst_column || '-'}
                    </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary">{candidate?.catalyst_attribute || '-'}</Typography>
                {showDefinition && (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>Definition</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{candidate?.catalyst_definition || '-'}</Typography>
                    </>
                )}
                {showReasoning && candidate?.llm_reasoning && (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>LLM Reasoning</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{candidate.llm_reasoning}</Typography>
                    </>
                )}
            </Box>
        );
        };
        const renderExpectedTargetTraceCard = () => {
            if (!expectedTargetTrace) return null;
            const mainFlow = expectedTargetTrace?.main_flow || {};
            const supplemental = expectedTargetTrace?.supplemental_validation || {};
            const targetCandidate = {
                ...(expectedTargetTrace?.candidate || {}),
                is_target: true,
                llm_confidence: typeof mainFlow?.llm_confidence === 'number' ? mainFlow.llm_confidence : null,
                llm_reasoning: mainFlow?.llm_reasoning || '',
                vector_score: typeof mainFlow?.semantic_score === 'number' ? mainFlow.semantic_score : null,
            };

            return (
                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                            Expected Target Tracking
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                            <Chip label={`Main Flow: ${mainFlow?.final_status || 'unknown'}`} color="primary" />
                            <Chip label={`LLM: ${mainFlow?.llm_status || 'not_evaluated'}`} variant="outlined" />
                            {mainFlow?.inferred_stage_label && <Chip label={mainFlow.inferred_stage_label} size="small" variant="outlined" />}
                            {Number.isInteger(mainFlow?.semantic_rank) && <Chip label={`Semantic rank #${mainFlow.semantic_rank}`} size="small" variant="outlined" />}
                            {Number.isInteger(mainFlow?.kept_bucket_position) && Number.isInteger(mainFlow?.kept_bucket_size) && (
                                <Chip label={`Bucket #${mainFlow.kept_bucket_position} of ${mainFlow.kept_bucket_size}`} size="small" variant="outlined" />
                            )}
                        </Stack>
                        {renderTraceCandidate(targetCandidate, { showDefinition: true, showReasoning: true })}
                        {mainFlow?.final_explanation && (
                            <Alert severity="info" sx={{ mt: 1.5 }}>
                                {mainFlow.final_explanation}
                            </Alert>
                        )}
                        {mainFlow?.not_in_bucket_reason && (
                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                {mainFlow.not_in_bucket_reason}
                            </Alert>
                        )}
                        {supplemental?.performed && (
                            <Alert severity={supplemental?.is_equivalent ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
                                <strong>Supplemental Expected-Target Review</strong>
                                <br />
                                {`${supplemental.is_equivalent ? 'Equivalent' : 'Not equivalent'} at ${formatConfidence(supplemental.confidence || 0)} confidence.`}
                                {supplemental?.reasoning ? ` ${supplemental.reasoning}` : ''}
                            </Alert>
                        )}
                        {!supplemental?.performed && supplemental?.error && (
                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                Supplemental expected-target review could not be completed: {supplemental.error}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            );
        };
        const renderPipelineAccordion = (panelKey, title, subtitle, content, options = {}) => {
            const isVisible = options.visible !== false;
            if (!isVisible) return null;
            const isActive = diagnosticLiveExpandedPanel === panelKey;
            return (
                <Accordion
                    key={panelKey}
                    expanded={isActive}
                    onChange={(event, expanded) => setDiagnosticLiveExpandedPanel(expanded ? panelKey : false)}
                    disableGutters
                    sx={{
                        border: '1px solid',
                        borderColor: activeStep?.key === panelKey ? 'primary.main' : 'divider',
                        borderRadius: '14px !important',
                        overflow: 'hidden',
                        boxShadow: activeStep?.key === panelKey ? '0 18px 42px rgba(25, 118, 210, 0.14)' : 'none',
                        '&:before': { display: 'none' },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            bgcolor: activeStep?.key === panelKey ? 'rgba(25, 118, 210, 0.06)' : 'background.paper',
                            minHeight: 72,
                        }}
                    >
                        <Box>
                            <Typography variant="h6" fontWeight={700}>{title}</Typography>
                            {subtitle ? (
                                <Typography variant="body2" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            ) : null}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        {content}
                    </AccordionDetails>
                </Accordion>
            );
        };

        return (
            <Stack spacing={3}>
                {renderDiagnosticSelectionCard(
                    'Pipeline Replay',
                    'Run the selected source-to-target pair through semantic retrieval, bucket assignment, and stage-by-stage validation.',
                    <Button variant="contained" size="small" onClick={runLivePipeline} disabled={!hasPairSelection || diagnosticLiveTraceLoading || diagnosticLivePipelineRunning}>
                        {diagnosticLivePipelineRunning ? 'Running...' : 'Submit'}
                    </Button>,
                )}

                {!hasPairSelection && <Alert severity="info">Select both a source field and a ground-truth target to run the live pipeline replay.</Alert>}

                {hasPairSelection && diagnosticLiveTraceLoading && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Running Live Trace</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{diagnosticLiveStatusMessage || 'Recomputing retrieval, bucket assignment, and LLM validation for the selected pair.'}</Typography>
                            <LinearProgress sx={{ height: 10, borderRadius: 999 }} />
                        </CardContent>
                    </Card>
                )}

                {hasPairSelection && diagnosticLiveTrace && (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} lg={3}>
                                {renderDiagnosticFieldCard('Source Field', sourceDetails, 'Select a source field.', '#1565c0')}
                            </Grid>
                            <Grid item xs={12} lg={3}>
                                {renderDiagnosticFieldCard('Selected Target', targetDetails, 'Select a target field.', '#6a1b9a')}
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <Card sx={{ height: '100%', background: 'linear-gradient(135deg, rgba(21,101,192,0.10), rgba(106,27,154,0.08))' }}>
                                    <CardContent>
                                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                                            <Chip label={diagnosticLiveTrace?.final_outcome?.status || 'pending'} color="primary" />
                                            {Number.isInteger(diagnosticLiveProgress?.current) && Number.isInteger(diagnosticLiveProgress?.total)
                                                ? <Chip label={`Overall ${diagnosticLiveProgress.current} of ${diagnosticLiveProgress.total}`} size="small" variant="outlined" />
                                                : <Chip label={`Step ${Math.min(diagnosticLiveReplayIndex + 1, Math.max(playbackSteps.length, 1))} of ${Math.max(playbackSteps.length, 1)}`} size="small" variant="outlined" />}
                                            <Chip label={`Display Top ${diagnosticLiveTrace?.settings?.top_k_to_display || 0}`} size="small" variant="outlined" />
                                        </Stack>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>Live Flow Progress</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                            {diagnosticLiveStatusMessage || activeStep?.label || 'Ready to replay the pipeline.'}
                                        </Typography>
                                        <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 12, borderRadius: 999, mb: 1.5 }} />
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {playbackSteps.map((step, index) => (
                                                <Chip key={step.key} label={step.label} color={index === diagnosticLiveReplayIndex ? 'primary' : (index < diagnosticLiveReplayIndex ? 'success' : 'default')} variant={index === diagnosticLiveReplayIndex ? 'filled' : 'outlined'} />
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {renderPipelineAccordion(
                            'exact_lookup',
                            'Exact Lookup',
                            diagnosticLiveTrace?.exact_lookup?.target_found
                                ? 'The selected target was found during exact lookup.'
                                : 'No exact match, so the flow continues into semantic retrieval.',
                            <>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                                    <Chip label={diagnosticLiveTrace?.exact_lookup?.target_found ? 'Exact target found' : 'No exact target match'} color={diagnosticLiveTrace?.exact_lookup?.target_found ? 'success' : 'default'} />
                                    {diagnosticLiveTrace?.exact_lookup?.stage_label && <Chip label={diagnosticLiveTrace.exact_lookup.stage_label} size="small" variant="outlined" />}
                                    {Number.isInteger(diagnosticLiveTrace?.exact_lookup?.bucket_position) && Number.isInteger(diagnosticLiveTrace?.exact_lookup?.bucket_size) && (
                                        <Chip label={`#${diagnosticLiveTrace.exact_lookup.bucket_position} of ${diagnosticLiveTrace.exact_lookup.bucket_size}`} size="small" variant="outlined" />
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {diagnosticLiveTrace?.exact_lookup?.target_found
                                        ? `The selected target was found during exact lookup${diagnosticLiveTrace?.exact_lookup?.exact_match_basis ? ` by ${diagnosticLiveTrace.exact_lookup.exact_match_basis}` : ''}.`
                                        : 'The selected target did not match exact lookup, so the flow continues into semantic retrieval.'}
                                </Typography>
                            </>,
                            { visible: isStepVisible('exact_lookup') },
                        )}

                        {renderPipelineAccordion(
                            'semantic_search',
                            'Stage 1: Semantic Search',
                            `Showing the top ${diagnosticLiveTrace?.semantic_search?.preview_limit || 20} retrieved candidates and where the selected target landed.`,
                            <>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                                    <Chip label={`Retrieved ${diagnosticLiveTrace?.semantic_search?.retrieved_candidate_count || 0}`} color="primary" variant="outlined" />
                                    <Chip label={diagnosticLiveTrace?.semantic_search?.target_found ? `Raw semantic rank #${diagnosticLiveTrace.semantic_search.target_rank}` : 'Target not retrieved'} color={diagnosticLiveTrace?.semantic_search?.target_found ? 'success' : 'warning'} variant="outlined" />
                                    {Number.isInteger(diagnosticLiveTrace?.semantic_search?.target_bucket_rank) && (
                                        <Chip
                                            label={`Bucket rank #${diagnosticLiveTrace.semantic_search.target_bucket_rank}${diagnosticLiveTrace?.semantic_search?.target_bucket_stage ? ` in ${getMatchStageLabel(diagnosticLiveTrace.semantic_search.target_bucket_stage)}` : ''}`}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                    <Chip label={typeof diagnosticLiveTrace?.semantic_search?.target_score === 'number' ? `Semantic ${formatConfidence(diagnosticLiveTrace.semantic_search.target_score)}` : 'No target score'} variant="outlined" />
                                </Stack>
                                <Grid container spacing={1.5}>
                                    {(diagnosticLiveTrace?.semantic_search?.preview_candidates || []).map((candidate) => (
                                        <Grid item xs={12} md={6} lg={4} key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-${candidate?.position || 0}`}>
                                            {renderTraceCandidate(candidate)}
                                        </Grid>
                                    ))}
                                </Grid>
                            </>,
                            { visible: isStepVisible('semantic_search') },
                        )}

                        {renderPipelineAccordion(
                            'bucketing',
                            'Stage 2: Buckets',
                            'Candidates are grouped by theme and priority stage, then ordered by semantic score within each bucket. The selected target stays highlighted inside the bucket previews.',
                            <>
                                {!diagnosticLiveTrace?.bucketing?.target_bucket_position && diagnosticLiveTrace?.bucketing?.not_in_bucket_reason && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>{diagnosticLiveTrace.bucketing.not_in_bucket_reason}</Alert>
                                )}
                                <Grid container spacing={2}>
                                    {(diagnosticLiveTrace?.bucketing?.stages || []).map((stage) => (
                                        <Grid item xs={12} md={6} xl={4} key={stage.stage_name}>
                                            <Card sx={{ height: '100%', border: '1px solid', borderColor: activeStage === stage.stage_name ? 'primary.main' : (stage.target_in_bucket ? 'success.light' : 'divider'), boxShadow: activeStage === stage.stage_name ? '0 16px 36px rgba(25, 118, 210, 0.16)' : 'none', transform: activeStage === stage.stage_name ? 'translateY(-4px)' : 'none', transition: 'all 220ms ease' }}>
                                                <CardContent>
                                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                                                        <Chip label={stage.stage_label} color={activeStage === stage.stage_name ? 'primary' : 'default'} />
                                                        <Chip label={`${stage.kept_count}/${stage.total_count} kept`} size="small" variant="outlined" />
                                                        {stage.target_in_bucket
                                                            ? <Chip label={`Target #${stage.target_position}`} size="small" color="success" variant="outlined" />
                                                            : (stage.target_total_position ? <Chip label={`Target overall #${stage.target_total_position}`} size="small" color="warning" variant="outlined" /> : null)}
                                                        {stage.target_raw_score_position && (
                                                            <Chip label={`Raw score rank #${stage.target_raw_score_position}`} size="small" color="secondary" variant="outlined" />
                                                        )}
                                                    </Stack>
                                                    <Stack spacing={1}>
                                                        {(stage.candidates || []).length > 0
                                                            ? stage.candidates.map((candidate) => renderTraceCandidate(candidate))
                                                            : <Typography variant="body2" color="text.secondary">No candidates in this bucket.</Typography>}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </>,
                            { visible: isStepVisible('bucketing') },
                        )}

                        {(diagnosticLiveTrace?.validation?.stages || [])
                            .filter((stage) => isStageVisible(stage.stage_name) || activeStep?.key === 'final_outcome')
                            .map((stage) => renderPipelineAccordion(
                                `validate_${stage.stage_name}`,
                                stage.stage_label,
                                stage.summary || 'No summary available.',
                                <>
                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                                        <Chip label={stage.validation_status} color={stage.target_result === 'approved' ? 'success' : (stage.target_result === 'rejected' ? 'error' : 'default')} />
                                        <Chip label={`${stage.candidate_count} in bucket`} size="small" variant="outlined" />
                                        <Chip label={`${stage.llm_submitted_count || 0} sent to LLM`} size="small" variant="outlined" />
                                        <Chip label={`${stage.llm_decision_count || 0} returned by LLM`} size="small" variant="outlined" />
                                        <Chip label={`${(stage.approved_matches || []).length} approved semantic`} size="small" variant="outlined" />
                                        {stage.target_in_stage && <Chip label={`Target #${stage.target_position}`} size="small" color="success" variant="outlined" />}
                                        {stage.ranking_status === 'ranked' && <Chip label="Fallback ranked" size="small" color="secondary" variant="outlined" />}
                                    </Stack>
                                    {stage.target_reasoning && <Alert severity={stage.target_result === 'approved' ? 'success' : 'warning'}>{stage.target_reasoning}</Alert>}
                                    {(stage.approved_matches || []).length > 0 && (
                                        <>
                                            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: stage.target_reasoning ? 2 : 0, mb: 1 }}>
                                                Approved Fields
                                            </Typography>
                                            <Grid container spacing={1.5} sx={{ mb: stage.ranked_matches?.length ? 2 : 0 }}>
                                                {(stage.approved_matches || []).map((candidate, index) => (
                                                    <Grid item xs={12} md={6} lg={4} key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-${index}`}>
                                                        {renderTraceCandidate(candidate)}
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </>
                                    )}
                                    {(stage.ranked_matches || []).length > 0 && (
                                        <>
                                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                                Ranked Fallback Fields
                                            </Typography>
                                            <Grid container spacing={1.5}>
                                                {(stage.ranked_matches || []).map((candidate, index) => (
                                                    <Grid item xs={12} md={6} lg={4} key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-ranked-${index}`}>
                                                        {renderTraceCandidate(candidate)}
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </>
                                    )}
                                </>,
                                { visible: true },
                            ))}

                        {renderPipelineAccordion(
                            'final_outcome',
                            'Final Outcome',
                            'Exact Matches and Semantic Approved Matches are shown independently.',
                            <>
                                <Alert severity={(finalExact?.target_in_displayed || finalSemantic?.target_in_displayed) ? 'success' : 'info'} sx={{ mb: 2 }}>
                                    {diagnosticLiveTrace?.final_outcome?.explanation || 'No outcome explanation available.'}
                                </Alert>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                                    <Chip label={diagnosticLiveTrace?.final_outcome?.status || 'unknown'} color="primary" />
                                    <Chip label={exactTargetLabel} color={finalExact?.target_in_displayed ? 'success' : (finalExact?.target_in_all ? 'warning' : 'default')} variant="outlined" />
                                    <Chip label={semanticTargetLabel} color={finalSemantic?.target_in_displayed ? 'success' : (finalSemantic?.target_in_all ? 'warning' : 'default')} variant="outlined" />
                                    <Chip label={`Exact ${finalExactDisplayed.length} shown`} color="primary" variant="outlined" />
                                    <Chip label={`Semantic ${finalSemanticDisplayed.length} shown`} color="secondary" variant="outlined" />
                                </Stack>
                                {replaySelectedLayerOutput && (
                                    <>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                            Selected Layer Output ({replaySelectedLayer})
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} lg={6}>
                                                {renderDiagnosticMatchBucket(replaySelectedLayerOutput, replaySelectedLayer, 'exact')}
                                            </Grid>
                                            <Grid item xs={12} lg={6}>
                                                {renderDiagnosticMatchBucket(replaySelectedLayerOutput, replaySelectedLayer, 'semantic')}
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                                {renderExpectedTargetTraceCard()}
                                {(finalExactDisplayed.length > 0 || finalExactOverflow.length > 0) && (
                                    <>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                                            <Chip label={`Display Top ${diagnosticLiveTrace?.final_outcome?.display_limit || 0}`} color="primary" variant="outlined" size="small" />
                                            <Chip label="Blue = shown in Exact top N" color="primary" variant="outlined" size="small" />
                                            <Chip label="Amber = Exact overflow" color="warning" variant="outlined" size="small" />
                                        </Stack>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                            Exact Matches
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            {finalExactDisplayed.map((candidate, index) => (
                                                <Grid item xs={12} md={6} lg={4} key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-exact-displayed-${index}`}>
                                                    {renderTraceCandidate(candidate, { finalDisplayState: 'displayed' })}
                                                </Grid>
                                            ))}
                                            {finalExactOverflow.map((candidate, index) => (
                                                <Grid item xs={12} md={6} lg={4} key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-exact-overflow-${index}`}>
                                                    {renderTraceCandidate(candidate, { finalDisplayState: 'skipped' })}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </>
                                )}
                                {finalExactDisplayed.length === 0 && finalExactOverflow.length === 0 && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        No exact matches were found for this source-target replay.
                                    </Alert>
                                )}
                                {(finalSemanticDisplayed.length > 0 || finalSemanticOverflow.length > 0) && (
                                    <>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: (finalExactDisplayed.length > 0 || finalExactOverflow.length > 0) ? 2.5 : 0, mb: 1.5 }}>
                                            <Chip label={`Display Top ${diagnosticLiveTrace?.final_outcome?.display_limit || 0}`} color="primary" variant="outlined" size="small" />
                                            <Chip label="Blue = shown in Semantic top N" color="primary" variant="outlined" size="small" />
                                            <Chip label="Amber = Semantic overflow" color="warning" variant="outlined" size="small" />
                                        </Stack>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                            Semantic Approved Matches
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            {finalSemanticDisplayed.map((candidate, index) => (
                                                <Grid item xs={12} md={6} lg={4} key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-semantic-displayed-${index}`}>
                                                    {renderTraceCandidate(candidate, { finalDisplayState: 'displayed' })}
                                                </Grid>
                                            ))}
                                            {finalSemanticOverflow.map((candidate, index) => (
                                                <Grid item xs={12} md={6} lg={4} key={`${candidate?.catalyst_table || 'unknown'}-${candidate?.catalyst_column || 'unknown'}-semantic-overflow-${index}`}>
                                                    {renderTraceCandidate(candidate, { finalDisplayState: 'skipped' })}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </>
                                )}
                                {finalSemanticDisplayed.length === 0 && finalSemanticOverflow.length === 0 && (
                                    <Alert severity="info">
                                        No semantic candidates were approved for this replay.
                                    </Alert>
                                )}
                            </>,
                            { visible: isStepVisible('final_outcome') },
                        )}
                    </>
                )}
            </Stack>
        );
    };

    if (loading) {
        return (
            <Box>
                <Skeleton variant="text" width={200} height={40} />
                <Skeleton variant="rectangular" height={400} sx={{ mt: 3, borderRadius: 2 }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link to="/schema-analyzer/projects" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>Projects</Typography>
                </Link>
                <Link to={`/schema-analyzer/projects/${projectId}/upload`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>{project?.name}</Typography>
                </Link>
                <Typography color="text.primary">Schema Mapper</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/schema-analyzer/projects/${projectId}/upload`)} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>Schema Mapper</Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/schema-analyzer/projects/${projectId}/upload`)}
                >
                    Schema Upload
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* File Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>Select Legacy File to Map</Typography>
                    {LEGACY_SYSTEMS.map(system => (
                        <Box key={system} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{system}</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {(files[system] || []).map(file => (
                                    <Chip
                                        key={file.id}
                                        label={file.file_name}
                                        onClick={() => handleSelectFile(system, file)}
                                        color={selectedFile?.id === file.id ? 'primary' : 'default'}
                                        variant={selectedFile?.id === file.id ? 'filled' : 'outlined'}
                                        icon={<TableIcon />}
                                    />
                                ))}
                                {(!files[system] || files[system].length === 0) && (
                                    <Typography variant="body2" color="text.secondary">No files uploaded</Typography>
                                )}
                            </Stack>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            {/* Selected File Actions */}
            {selectedFile && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <Typography variant="h6" fontWeight={600}>{selectedFile.file_name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedFile.table_count} tables, {selectedFile.column_count} columns
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={mappingSummaryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    onClick={() => setMappingSummaryOpen((open) => !open)}
                                >
                                    {mappingSummaryOpen ? 'Hide details' : 'Show details'}
                                </Button>
                                {mappingData && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleDeleteMappings}
                                        size="small"
                                    >
                                        Delete Mappings
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <ProcessIcon />}
                                    onClick={handleProcessFile}
                                    disabled={processing}
                                >
                                    {processing ? 'Processing...' : showReprocessLabel ? 'Re-process' : 'Process Mappings'}
                                </Button>
                            </Box>
                        </Box>
                        <Collapse in={mappingSummaryOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 3 }}>
                                {/* Processing Status */}
                                {processingStatus && (
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Chip
                                                label={getProgressPhaseLabel(processingStatus.phase || processingStatus.status)}
                                                size="small"
                                                color={processing ? 'primary' : (processingStatus.status === 'completed' ? 'success' : 'default')}
                                            />
                                            <Typography variant="caption">
                                                {processing
                                                    ? `Elapsed: ${formatElapsedTime(processingStatus.elapsed_seconds)}`
                                                    : `Total Time: ${formatDurationMs(processingStatus.processing_time_ms)}`}
                                            </Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={processingStatus.progress || 0} sx={{ height: 8, borderRadius: 1, mb: 1 }} />
                                        {processingStatus?.latest_batch_metrics?.batch_number ? (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                                Last batch {processingStatus.latest_batch_metrics.batch_number}/{processingStatus.latest_batch_metrics.total_batches}
                                                {`: ${formatDurationMs(processingStatus.latest_batch_metrics.total_ms)}`}
                                            </Typography>
                                        ) : null}
                                        {Array.isArray(processingStatus?.batch_timing_history) && processingStatus.batch_timing_history.length > 0 ? (
                                            <Box sx={{ mt: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    startIcon={<InfoIcon />}
                                                    onClick={() => setBatchTimingDialogOpen(true)}
                                                >
                                                    Batch Timing History
                                                </Button>
                                            </Box>
                                        ) : null}
                                        {STAGE_COUNT_CHIPS.some((item) => processingStatus?.[item.key] !== undefined) && (
                                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {STAGE_COUNT_CHIPS.map((item) => (
                                                    <Chip
                                                        key={item.key}
                                                        label={(
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Typography variant="inherit" component="span">
                                                                    {`${item.label}: ${processingStatus?.[item.key] || 0}`}
                                                                </Typography>
                                                                <Tooltip title={item.info} arrow>
                                                                    <InfoIcon sx={{ fontSize: 14 }} />
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                        size="small"
                                                        variant="outlined"
                                                        color={item.color}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Collapse>

                    </CardContent>
                </Card>
            )}

            {selectedFile && mappingData && (
                <Card sx={{ mb: 3 }}>
                    <Tabs
                        value={activeView}
                        onChange={(event, value) => setActiveView(value)}
                        sx={{ px: 2, pt: 1 }}
                    >
                        <Tab value="results" label="Mapping Results" />
                        <Tab value="pipeline" label="Pipeline Replay" />
                    </Tabs>
                </Card>
            )}

            {/* Filters & Pagination */}
            {mappingData && activeView === 'results' && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={2.5}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    openOnFocus
                                    options={mappingData.tables || []}
                                    value={tableFilter || null}
                                    onChange={(event, newValue) => {
                                        setTableFilter(newValue || '');
                                        setColumnFilter(''); // Reset column filter when table changes
                                        setPage(1);
                                    }}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderInput={(params) => <TextField {...params} label="Table" placeholder="Select table..." />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.5}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    openOnFocus
                                    options={mappingData.columns_list || []}
                                    value={columnFilter || null}
                                    onChange={(event, newValue) => {
                                        setColumnFilter(newValue || '');
                                        setPage(1);
                                    }}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderInput={(params) => <TextField {...params} label="Column" placeholder="Select column..." />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={1.5}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                        label="Status"
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="accepted">Accepted</MenuItem>
                                        <MenuItem value="rejected">Rejected</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Match Type</InputLabel>
                                    <Select
                                        value={matchStageFilter}
                                        onChange={(e) => { setMatchStageFilter(e.target.value); setPage(1); }}
                                        label="Match Type"
                                    >
                                        <MenuItem value="">Any Match</MenuItem>
                                        {MATCH_STAGE_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={3.5} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                                    size="small"
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    sx={{ mr: 1 }}
                                >
                                    {downloading ? 'Downloading...' : 'Download'}
                                </Button>
                                <Pagination
                                    count={mappingData.pagination?.total_pages || 1}
                                    page={page}
                                    onChange={(e, v) => setPage(v)}
                                    color="primary"
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Column Mappings Table */}
            {activeView === 'pipeline' && selectedFile && (
                <Box sx={{ mb: 3 }}>
                    {renderPipelineSection()}
                </Box>
            )}

            {mappingData && mappingData.columns && activeView === 'results' && (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 700, width: '16%' }}>Legacy Column</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '12%' }}>Attribute</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '8%' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '14%' }}>Legacy Definition</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '22%' }}>Target Column</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '6%' }}>Score</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '12%', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mappingData.columns.map((col) => {
                                const activeLayer = rowLayerFilter[col.column_key] || getDefaultLayerForColumn(col);
                                const layerDecision = getLayerDecision(col, activeLayer);
                                const matchView = getVisibleLayerMatches(col, activeLayer);
                                const matches = matchView.matches;
                                const totalAvailableMatches = matchView.totalAvailable;
                                const shownMatchCount = matchView.shownCount;
                                const canLoadMoreMatches = matchView.canLoadMore;
                                const canShowLessMatches = matchView.canShowLess;
                                const activePath = matchView.activePath;
                                const showMatchFooter = totalAvailableMatches > 0;
                                const rowSpan = matches.length + (showMatchFooter ? 1 : 0);

                                const matchRows = matches.map((match, mIdx) => {
                                    const resolvedStatus = getResolvedMatchDecisionStatus(layerDecision, match, activeLayer);
                                    const isSelected = resolvedStatus === 'accepted';
                                    const isRejected = resolvedStatus === 'rejected';

                                    return (
                                        <TableRow
                                            key={`${col.column_key}-${activeLayer}-${match?.matchPath || 'none'}-${match?.catalyst_table || 'placeholder'}-${match?.catalyst_column || mIdx}`}
                                            sx={{
                                                '&:hover': { bgcolor: 'action.hover' },
                                                bgcolor: isSelected
                                                    ? 'rgba(76, 175, 80, 0.04)'
                                                    : isRejected
                                                        ? 'rgba(211, 47, 47, 0.04)'
                                                        : 'inherit'
                                            }}
                                        >
                                            {mIdx === 0 && (
                                                <>
                                                    <TableCell rowSpan={rowSpan} sx={{ borderRight: '1px solid #eee', py: 2 }}>
                                                        <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ wordBreak: 'break-all' }}>
                                                            {col.table_name}.<Typography component="span" fontWeight={800}>{col.column_name}</Typography>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell rowSpan={rowSpan} sx={{ borderRight: '1px solid #eee', py: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                                                            {col.legacy_attribute || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell rowSpan={rowSpan} sx={{ borderRight: '1px solid #eee' }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                            {col.legacy_data_type || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell rowSpan={rowSpan} sx={{ borderRight: '1px solid #eee' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                            <Typography variant="caption" sx={{
                                                                lineHeight: 1.3,
                                                                color: 'text.secondary',
                                                                flex: 1,
                                                            }}>
                                                                {getLegacyDefinitionPreview(col.legacy_definition)}
                                                            </Typography>
                                                            {col.legacy_definition && col.legacy_definition.length > LEGACY_DEFINITION_PREVIEW_LENGTH && (
                                                                <Tooltip title="View full definition" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ p: 0.25, mt: -0.25, flexShrink: 0 }}
                                                                        onClick={() => {
                                                                            setDefDialogContent({
                                                                                title: `${col.table_name}.${col.column_name}`,
                                                                                definition: col.legacy_definition,
                                                                            });
                                                                            setDefDialogOpen(true);
                                                                        }}
                                                                    >
                                                                        <InfoIcon sx={{ fontSize: 14, color: 'primary.light' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </>
                                            )}

                                            {match && !match.isPlaceholder ? (
                                                <>
                                                    <TableCell sx={{ color: isSelected ? 'success.main' : isRejected ? 'error.main' : 'inherit', py: 1 }}>
                                                        {mIdx === 0 && (
                                                            <Box sx={{ mb: 0.5 }}>
                                                                {renderLayerToggle(col, activeLayer)}
                                                                {renderMatchPathToggle(col, activeLayer)}
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                            <Typography variant="body2" fontWeight={isSelected ? 700 : isRejected ? 700 : 500} sx={{ wordBreak: 'break-all' }}>
                                                                {match.catalyst_table}.{match.catalyst_column}
                                                            </Typography>
                                                            <Tooltip title={renderMatchInfoTooltip(col, match)} arrow>
                                                                <InfoIcon sx={{ fontSize: 14, color: 'primary.light', cursor: 'help' }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                                                            {isSelected && <Chip label="Accepted" size="small" color="success" sx={{ height: 16, fontSize: '0.6rem' }} />}
                                                            {isRejected && <Chip label="Rejected" size="small" color="error" sx={{ height: 16, fontSize: '0.6rem' }} />}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {typeof match.llm_confidence === 'number' ? (
                                                                <Chip
                                                                    label={`${(getDisplayMatchConfidence(match) * 100).toFixed(0)}%`}
                                                                    size="small"
                                                                    color={getScoreColor(getDisplayMatchConfidence(match))}
                                                                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                                                                />
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">-</Typography>
                                                            )}
                                                            {match.llm_reasoning && (
                                                                <Tooltip title="View LLM reasoning" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ p: 0.25 }}
                                                                        onClick={() => {
                                                                            setDefDialogContent({
                                                                                title: `${match.catalyst_table}.${match.catalyst_column} - LLM Reasoning`,
                                                                                definition: [
                                                                                    match.llm_reasoning || '',
                                                                                    Array.isArray(match.llm_concerns) && match.llm_concerns.length
                                                                                        ? `\n\nConcerns:\n- ${match.llm_concerns.join('\n- ')}`
                                                                                        : '',
                                                                                ].join(''),
                                                                            });
                                                                            setDefDialogOpen(true);
                                                                        }}
                                                                    >
                                                                        <InfoIcon sx={{ fontSize: 14, color: 'primary.light' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ verticalAlign: 'top', py: 1 }}>
                                                        <Stack spacing={0.5} alignItems="center" sx={{ pt: mIdx === 0 ? 4 : 0.25 }}>
                                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                                                                <Tooltip title="Manual Override">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => openManualSelectionDialog(col, activeLayer, match?.matchPath || match?.match_path, match)}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={isSelected ? "Remove Accepted Match" : "Accept Match"}>
                                                                    <IconButton
                                                                        size="small"
                                                                        color={isSelected ? "success" : "default"}
                                                                        onClick={() => handleDecision(col, activeLayer, match, isSelected ? 'pending' : 'accepted')}
                                                                    >
                                                                        <AcceptIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={isRejected ? "Clear Rejection" : "Reject Match"}>
                                                                    <IconButton
                                                                        size="small"
                                                                        color={isRejected ? "error" : "default"}
                                                                        onClick={() => handleDecision(col, activeLayer, match, isRejected ? 'pending' : 'rejected')}
                                                                    >
                                                                        <RejectIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </Stack>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell sx={{ color: isSelected ? 'success.main' : isRejected ? 'error.main' : 'inherit', py: 1 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                            {mIdx === 0 && (
                                                                <>
                                                                    {renderLayerToggle(col, activeLayer)}
                                                                    {renderMatchPathToggle(col, activeLayer)}
                                                                </>
                                                            )}
                                                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                                                {match?.placeholderMessage || (match?.layerCaption ? `${match.layerCaption}: No matches found` : 'No matches found')}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            -
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ verticalAlign: 'top', py: 1 }}>
                                                        <Stack spacing={0.5} alignItems="center" sx={{ pt: mIdx === 0 ? 4 : 0.25 }}>
                                                            <Tooltip title="Manual Override">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => openManualSelectionDialog(col, activeLayer, match?.matchPath, null)}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    );
                                });

                                if (showMatchFooter) {
                                    matchRows.push(
                                        <TableRow
                                            key={`${col.column_key}-${activeLayer}-footer`}
                                            sx={{ bgcolor: 'rgba(0, 0, 0, 0.015)' }}
                                        >
                                            <TableCell sx={{ py: 0.75, borderTop: '1px dashed #e0e0e0' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
                                                    {renderMatchVisibilitySummary(shownMatchCount, totalAvailableMatches)}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75, borderTop: '1px dashed #e0e0e0' }}>
                                                <Box sx={{ minHeight: 28 }} />
                                            </TableCell>
                                            <TableCell align="center" sx={{ py: 0.75, borderTop: '1px dashed #e0e0e0' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 28 }}>
                                                    {renderMatchVisibilityControls(
                                                        col,
                                                        activeLayer,
                                                        activePath,
                                                        canLoadMoreMatches,
                                                        canShowLessMatches,
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }

                                return matchRows;
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )
            }

            {/* Bottom Pagination */}
            {
                mappingData && activeView === 'results' && mappingData.pagination?.total_pages > 1 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={mappingData.pagination.total_pages}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                            color="primary"
                        />
                    </Box>
                )
            }

            <Dialog
                open={batchTimingDialogOpen}
                onClose={() => setBatchTimingDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    Batch Timing History
                </DialogTitle>
                <DialogContent dividers>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Batch</TableCell>
                                    <TableCell align="right">Size</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Retrieve</TableCell>
                                    <TableCell align="right">Validate</TableCell>
                                    <TableCell align="right">Rank</TableCell>
                                    <TableCell align="right">Apply</TableCell>
                                    <TableCell align="right">LLM Calls</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(Array.isArray(processingStatus?.batch_timing_history) ? processingStatus.batch_timing_history : []).map((item) => (
                                    <TableRow key={`batch-timing-${item.batch_number}`}>
                                        <TableCell>{item.batch_number}/{item.total_batches}</TableCell>
                                        <TableCell align="right">{item.batch_size || 0}</TableCell>
                                        <TableCell align="right">{formatDurationMs(item.total_ms)}</TableCell>
                                        <TableCell align="right">{formatDurationMs(item.retrieval_ms)}</TableCell>
                                        <TableCell align="right">{formatDurationMs(item.validation_ms)}</TableCell>
                                        <TableCell align="right">{formatDurationMs(item.ranking_ms)}</TableCell>
                                        <TableCell align="right">{formatDurationMs(item.apply_ms)}</TableCell>
                                        <TableCell align="right">{item.llm_calls_in_batch || 0}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBatchTimingDialogOpen(false)} size="small">Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={manualDialogOpen}
                onClose={() => {
                    if (manualSaving) return;
                    setManualDialogOpen(false);
                    setManualDialogColumn(null);
                    setManualDialogPath('semantic');
                    setManualDialogSourceMatch(null);
                    setManualTable(null);
                    setManualColumn(null);
                    setManualTables([]);
                    setManualColumns([]);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    Manual {manualDialogLayer} Mapping
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {manualDialogColumn ? `${manualDialogColumn.table_name}.${manualDialogColumn.column_name}` : ''}
                    </Typography>
                    <Stack spacing={2}>
                        <Autocomplete
                            options={manualTables}
                            loading={manualLoadingTables}
                            value={manualTable}
                            onChange={(event, newValue) => handleManualTableChange(newValue, manualDialogLayer)}
                            getOptionLabel={(option) => option?.table_name || ''}
                            isOptionEqualToValue={(option, value) => option?.table_name === value?.table_name}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={`${manualDialogLayer} Table`}
                                    placeholder="Search table..."
                                />
                            )}
                        />
                        <Autocomplete
                            options={manualColumns}
                            loading={manualLoadingColumns}
                            value={manualColumn}
                            onChange={(event, newValue) => setManualColumn(newValue)}
                            getOptionLabel={(option) => option?.column_name || ''}
                            isOptionEqualToValue={(option, value) => (
                                option?.table_name === value?.table_name && option?.column_name === value?.column_name
                            )}
                            disabled={!manualTable}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={`${manualDialogLayer} Column`}
                                    placeholder="Search column..."
                                />
                            )}
                        />
                        {manualColumn?.definition && (
                            <Alert severity="info" sx={{ py: 0 }}>
                                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {manualColumn.definition}
                                </Typography>
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setManualDialogOpen(false);
                            setManualDialogColumn(null);
                            setManualDialogPath('semantic');
                            setManualDialogSourceMatch(null);
                            setManualTable(null);
                            setManualColumn(null);
                            setManualTables([]);
                            setManualColumns([]);
                        }}
                        size="small"
                        disabled={manualSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveManualSelection}
                        size="small"
                        variant="contained"
                        disabled={!manualTable || !manualColumn || manualSaving}
                        startIcon={manualSaving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
                    >
                        {manualSaving ? 'Saving...' : 'Save Mapping'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Legacy Definition Dialog */}
            <Dialog open={defDialogOpen} onClose={() => setDefDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    Legacy Definition — {defDialogContent.title}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {defDialogContent.definition}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDefDialogOpen(false)} size="small">Close</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
}

export default SchemaMapperPage;
